//src/pages/inPatientDetail/nurseKardex.jsx;
import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  FormHelperText,
  Table,
  TableHead,
  TableBody,
  TableCell,
  TableRow,
  Chip,
} from "@mui/material";
import { getByPatientAndAdmission } from "../../services/KardexVitalSignsService";
import * as Yup from "yup";
import { useFormik } from "formik";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import BasicModal from "../../components/BasicModal";
import FormModalActions from "../../components/FormModalActions";
import { LARGE_FORM_MODAL_SX, STANDARD_FORM_HEADING_SX } from "../../utils/formModalStyles";
import IconButton from '@mui/material/IconButton';
import DeleteIcon from '@mui/icons-material/Delete';
import SendIcon from "@mui/icons-material/Send";
import MedicationIcon from "@mui/icons-material/Medication";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import UndoIcon from "@mui/icons-material/Undo";
import { useAuth } from "../../context/AuthContext.jsx";
import { getPatientById } from "../../services/patientService";
import { create } from "../../services/KardexMedicalHistoryService";
import { createVitalSigns } from "../../services/KardexVitalSignsService";
import { createDietPlans } from "../../services/KardexDietPlansService";
import { createElimination } from "../../services/KardexEliminationService";
import { createInvestigations } from "../../services/KardexInvestigationService";
import { createCare } from "../../services/KardexNursingCareService";
import { createProcedures } from "../../services/KardexDeviceService";
import { createMobility } from "../../services/KardexMobilityService";
import { createShiftNotes } from "../../services/KardexNurseNotesService";
import { createCommunication } from "../../services/KardexPatientCommunicationService";
import { createMisc } from "../../services/KardexMiscAlertsService";
import { createMedicationsWithoutInventoryDeduction } from "../../services/KardexMedicationsService";
import { createIVFluids } from "../../services/KardexIvFluidsService";
import { getMedicineList } from "../../services/medicineService";
import { getAllMedicine } from "../../services/medicineNewService";
import { getLabtestsList, getAllLabTests } from "../../services/labtestService";
import { getById } from "../../services/PatientAdmissionService";
import { getAssignmentByAdmissionId } from "../../services/PatientAssignmentService";
import { getTreatmentsByAdmissionId } from "../../services/PatientTreatmentService";
import { getDoctorById } from "../../services/doctorService";
import { getStaffByUserId, getAllStaff } from "../../services/StaffService";
import { getAllWards } from "../../services/WardService";
import { getAllBeds } from "../../services/BedService";
import { 
  getRequestsByAdmissionId,
  receiveMedicineRequest,
  dispenseMedicineRequest,
  returnMedicineRequest
} from "../../services/medicineRequestService";
import { 
  getByAdmissionId as getPatientMedicineInventory,
  returnToPharmacy as returnPatientMedicineToPharmacy
} from "../../services/patientMedicineInventoryService";
import MedicineRequestModal from "../inventory/Pharmacy/MedicineRequestModal";
import MedicineReturnModal from "../inventory/Pharmacy/MedicineReturnModal";
import PatientMedicineAdministrationModal from "../inventory/Pharmacy/PatientMedicineAdministrationModal";
import { filterByOrganization } from "../../utils/organizationUtils";

dayjs.extend(utc);

const SECTION_OPTIONS = [
  "Patient Demographics",
  "Medical History",
  "Vital Signs Monitoring",
  "Medications",
  "IV Fluids",
  "Nutrition / Diet Plan",
  "Elimination / Output",
  "Investigations",
  "Nursing Care Plan",
  "Devices / Procedures",
  "Activity / Mobility",
  "Shift-wise Nursing Notes",
  "Patient Education & Communication",
  "Miscellaneous / Alerts",
];

const sectionFieldsMap = {
  "Medical History": [
    "pastMedicalHistory",
    "pastSurgicalHistory",
    "allergies",
    "fallOrSeizureRisk",
    "infectionPrecautions",
  ],

  "Vital Signs Monitoring": ["vitalSignsList"],

  "Nutrition / Diet Plan": ["dietPlanList"],

  "Elimination / Output": ["eliminationList"],

  "Investigations": ["investigationsList"],

  "Nursing Care Plan": ["nursingCareList"],

  "Devices / Procedures": ["deviceProceduresList"],

  "Activity / Mobility": [
    "mobilityLevel",
    "turningSchedule",
    "fallRiskPrecautions",
  ],

  "Shift-wise Nursing Notes": ["shift", "observationsInterventions"],

  "Patient Education & Communication": [
    "educationProvided",
    "familyCounseling",
  ],

  "Miscellaneous / Alerts": ["specialNotes", "dnr", "dischargePlans"],

  "Medications": ["medicationsList"],

  "IV Fluids": ["ivFluidsList"],
};

const medicalHistoryValidationSchema = Yup.object({
  sectionName: Yup.string().required("Section is required"),
  pastMedicalHistory: Yup.string().matches(/^[A-Za-z\s]+$/, "Only alphabets are allowed").when("sectionName", {
    is: "Medical History",
    then: (schema) => schema.required("Past Medical History is required"),
  }),
  pastSurgicalHistory: Yup.string().matches(/^[A-Za-z\s]+$/, "Only alphabets are allowed").when("sectionName", {
    is: "Medical History",
    then: (schema) => schema.required("Past Surgical History is required"),
  }),
  allergies: Yup.string().matches(/^[A-Za-z\s]+$/, "Only alphabets are allowed").when("sectionName", {
    is: "Medical History",
    then: (schema) => schema.required("Allergies are required"),
  }),
  fallOrSeizureRisk: Yup.string().matches(/^[A-Za-z\s]+$/, "Only alphabets are allowed").when("sectionName", {
    is: "Medical History",
    then: (schema) => schema.required("Fall/Seizure Risk is required"),
  }),
  infectionPrecautions: Yup.string().matches(/^[A-Za-z\s]+$/, "Only alphabets are allowed")
    .when("sectionName", {
      is: "Medical History",
      then: (schema) => schema.required("Infection Precautions are required"),
  }),
});

const vitalSignsValidationSchema = Yup.object().shape({
  vitalSignsList: Yup.array()
    .of(
      Yup.object().shape({
        bp: Yup.string().matches(/^\d+(\.\d+)?$/, "Only numeric or decimal values are allowed").required("BP is required"),
        temperature: Yup.string().matches(/^\d+(\.\d+)?$/, "Only numeric or decimal values are allowed").required("Temperature is required"),
        heartRate: Yup.string().matches(/^\d+(\.\d+)?$/, "Only numeric or decimal values are allowed").required("Heart Rate is required"),
        respiratoryRate: Yup.string().matches(/^\d+(\.\d+)?$/, "Only numeric or decimal values are allowed").required("Respiratory Rate is required"),
        spo2: Yup.string().matches(/^\d+(\.\d+)?$/, "Only numeric or decimal values are allowed").required("SpO₂ is required"),
        painScore: Yup.string().matches(/^\d+(\.\d+)?$/, "Only numeric or decimal values are allowed").required("Pain Score is required"),
        gcsScore: Yup.string().matches(/^\d+(\.\d+)?$/, "Only numeric or decimal values are allowed").required("GCS is required"),
      })
    )
    .min(1, "Add at least one vital sign entry"),
});

const dietPlanValidationSchema = Yup.object().shape({
  dietPlanList: Yup.array()
    .of(
      Yup.object().shape({
        dietType: Yup.string().matches(/^[A-Za-z\s]+$/, "Only alphabets are allowed").required("Diet Type is required"),
        feedingMethod: Yup.string().matches(/^[A-Za-z\s]+$/, "Only alphabets are allowed").required("Feeding Method is required"),
        fluidRestriction: Yup.string().matches(/^[A-Za-z\s]+$/, "Only alphabets are allowed").required("Fluid Restriction is required")}))
    .min(1, "Add at least one diet plan entry"),
});

const eliminationValidationSchema = Yup.object().shape({
  eliminationList: Yup.array()
    .of(
      Yup.object().shape({
        urine: Yup.string().matches(/^[A-Za-z\s]+$/, "Only alphabets are allowed").required("Urine is required"),
        stool: Yup.string().matches(/^[A-Za-z\s]+$/, "Only alphabets are allowed").required("Stool is required"),
        vomitingOrDrains: Yup.string().matches(/^[A-Za-z\s]+$/, "Only alphabets are allowed").required("Vomiting / Drains is required"),
      })
    )
    .min(1, "Add at least one elimination entry"),
});

const investigationsValidationSchema = Yup.object().shape({
  investigationsList: Yup.array()
    .of(
      Yup.object().shape({
        testName: Yup.string().required("Test Name is required"),
        status: Yup.string().required("Status is required"),
        importantResults: Yup.string().required("Important Results are required"),
      })
    )
    .min(1, "Add at least one investigation entry"),
});

const nursingCareValidationSchema = Yup.object().shape({
  nursingCareList: Yup.array()
    .of(
      Yup.object().shape({
        problem: Yup.string().required("Problem is required"),
        intervention: Yup.string().required("Intervention is required"),
        goal: Yup.string().required("Goal is required"),
      })
    )
    .min(1, "Add at least one nursing care entry"),
});

const deviceProceduresValidationSchema = Yup.object().shape({
  deviceProceduresList: Yup.array()
    .of(
      Yup.object().shape({
        ivLines: Yup.string().required("IV Lines info is required"),
        catheters: Yup.string().required("Catheters info is required"),
        tubes: Yup.string().required("Tubes info is required"),
        drainSites: Yup.string().required("Drain Sites info is required"),
      })
    )
    .min(1, "Add at least one device procedure entry"),
});

const mobilityValidationSchema = Yup.object().shape({
  mobilityLevel: Yup.string().required("Mobility Level is required"),
  turningSchedule: Yup.string().required("Turning Schedule is required"),
  fallRiskPrecautions: Yup.string().required("Fall Risk Precautions are required"),
});

const shiftNotesValidationSchema = Yup.object().shape({
  shift: Yup.string().required("Shift is required"),
  observationsInterventions: Yup.string()
  .required("Observations / Interventions are required")
  .matches(/^[A-Za-z0-9\s.,/(){}\[\]#@!%&*\-_:]+$/, "Only alphanumeric and common special characters are allowed"),
});

const educationCommunicationValidationSchema = Yup.object().shape({
  educationProvided: Yup.string()
    .required("Education Provided is required").matches(/^[A-Za-z0-9\s.,/(){}\[\]#@!%&*\-_:]+$/, "Only alphanumeric and common special characters are allowed")
    .max(255, "Maximum 255 characters allowed").matches(/^[A-Za-z0-9\s.,/(){}\[\]#@!%&*\-_:]+$/, "Only alphanumeric and common special characters are allowed"),

  familyCounseling: Yup.string()
    .required("Family Counseling is required")
    .max(255, "Maximum 255 characters allowed"),
});

const miscellaneousValidationSchema = Yup.object().shape({
  specialNotes: Yup.string()
    .required("Special Notes are required")
    .max(1000, "Maximum 1000 characters allowed"),
  dnr: Yup.string()
    .required("DNR status is required")
    .max(255, "Maximum 255 characters allowed"),
  dischargePlans: Yup.string()
    .required("Discharge Plans are required")
    .max(1000, "Maximum 1000 characters allowed"),
});

const medicationsValidationSchema = Yup.object().shape({
  medicationsList: Yup.array()
    .of(
      Yup.object().shape({
        medicationName: Yup.string().required("Medication Name is required"),
        dose: Yup.string().matches(/^\d+(\.\d+)?$/, "Only numeric or decimal values are allowed").required("Dose is required"),
        route: Yup.string().required("Route is required"),
        timeGiven: Yup.string().required("Time is required"),
      })
    )
    .min(1, "At least one medication must be added"),
});

const ivFluidsValidationSchema = Yup.object().shape({
  ivFluidsList: Yup.array()
    .of(
      Yup.object().shape({
        fluidType: Yup.string().required("Type is required"),
        rate: Yup.string().required("Rate is required"),
        site: Yup.string().required("Site is required"),
        startTime: Yup.string().required("Start - Stop Time is required"),
      })
    )
    .min(1, "Add at least one IV fluid entry"),
});

const NurseKardexForm = ({ open, onClose, admissionId, patientId }) => {
  
  const { user } = useAuth();
  const [selectedSection, setSelectedSection] = useState("");
  const [patient_info, setPatientInfo] = useState("");
  const [admission_info, setAdmissionInfo] = useState("");
  const [assignment_info, setAssignmentInfo] = useState([]);
  const [medicine_list, setMedicine] = useState([]);
  const [ivFluidsListFromBase, setIvFluidsListFromBase] = useState([]); // IV fluids from base raw medicines table
  const [labTestList, setLabTestList] = useState([]);
  const [labTestListFromBase, setLabTestListFromBase] = useState([]); // Lab tests from base lab test table
  const [vitalSignsList, setVitalSignsList] = useState([
    {
      bp: "",
      temperature: "",
      heartRate: "",
      respiratoryRate: "",
      spo2: "",
      painScore: "",
      gcs: ""
    },
  ]);
  
  // Medicine Management states
  const [treatmentList, setTreatmentList] = useState([]);
 console.log("treatment lists " ,treatmentList);
 
// 🔥 ADD HERE
const medicationTreatments = treatmentList.filter(
  (t) => t.treatmentType === "Medicine"
);

const ivFluidTreatments = treatmentList.filter(
  (t) => t.treatmentType === "IV Fluids"
);

console.log("IV Fluids list", ivFluidTreatments);

  const [medicineRequests, setMedicineRequests] = useState({});
  const [patientMedicineInventory, setPatientMedicineInventory] = useState([]);
  const [assignmentDetails, setAssignmentDetails] = useState(null);
  const [currentStaffId, setCurrentStaffId] = useState(null);
  const [openRequestModal, setOpenRequestModal] = useState(false);
  const [selectedRequestTreatment, setSelectedRequestTreatment] = useState(null);
  const [openReturnModal, setOpenReturnModal] = useState(false);
  const [selectedReturnTreatment, setSelectedReturnTreatment] = useState(null);
  const [openAdministrationModal, setOpenAdministrationModal] = useState(false);
  const [selectedInventoryItem, setSelectedInventoryItem] = useState(null);
  const [selectedAdministrationRequest, setSelectedAdministrationRequest] = useState(null);
  const [vitalList, setVitalList] = useState({});

  useEffect(() => {
    // Guard against null/undefined admissionId to avoid 500s
    if (admissionId != null && admissionId !== "null") {
      fetchAssignmentDetails();
      fetchAdmissionDetails();
      fetchMedicineManagementData();
    }
    fetchLabTestList();
    fetchLabTestsFromBase(); // Fetch lab tests from base lab test table
    getRawMedicine();
    fetchIvFluidsFromBase(); // Fetch IV fluids from base raw medicines table
    if (patientId != null) {
      fetchPatientDetails();
    }
    fetchCurrentStaffId();
    setSelectedSection("Patient Demographics");
    formik.setFieldValue("sectionName", "Patient Demographics");
     // ✅ ADD THIS PART
  if (patient_info?.id && admissionId) {
    fetchVitalSigns();
  }
  }, [open, admissionId, patient_info?.id]);
  // [open, admissionId]);

  const handleRequestIVFluid = (treatment) => {
  handleRequestMedicine(treatment);
};

const handleReceiveIVFluid = (treatment, inventoryItem) => {
  handleReceiveMedicine(treatment, inventoryItem);
};

const handleAdministerIVFluid = (treatment, inventoryItem) => {
  handleDispenseToPatient(treatment, inventoryItem);
};

const handleReturnPatientIVFluid = (treatment, inventoryItem, request) => {
  handleReturnPatientMedicine(treatment, inventoryItem, request);
};

  const getRawMedicine = async () => {
    try {
      const data = await getMedicineList();
      const medicineArray = Array.isArray(data) ? data : [];
      // Filter by organization to ensure only organization-specific medicines are shown
      const filteredMedicine = filterByOrganization(medicineArray, user);
      setMedicine(filteredMedicine);
    } catch (error) {
      console.error("Error fetching medicines:", error);
      setMedicine([]); // Set empty array on error
    }
  };

  // Fetch IV fluids from base raw medicines table (shared across all organizations)
  const fetchIvFluidsFromBase = async () => {
    try {
      const data = await getAllMedicine(); // This fetches from base table without organizationId
      const medicineArray = Array.isArray(data) ? data : [];
      
      // Filter for IV fluids from base raw medicines table
      const ivFluids = medicineArray.filter((m) => {
        // Handle different possible field names and values for fluid/IV fluid medicines
        const medicineType = m.medicineType || m.type || m.category || m.medicineDetails || "";
        const typeLower = String(medicineType).toLowerCase().trim();
        // Match various possible values: "fluid", "fluids", "iv fluid", "iv-fluids", "iv fluids", etc.
        return typeLower === "fluid" || 
               typeLower === "fluids" || 
               typeLower === "iv fluid" || 
               typeLower === "iv-fluids" || 
               typeLower === "iv fluids" ||
               typeLower.includes("fluid");
      });
      
      setIvFluidsListFromBase(ivFluids);
    } catch (error) {
      console.error("Error fetching IV fluids from base table:", error);
      setIvFluidsListFromBase([]); // Set empty array on error
    }
  };

  // Fetch lab tests from base lab test table (shared across all organizations)
  const fetchLabTestsFromBase = async () => {
    try {
      const data = await getAllLabTests(); // This fetches from base table without organizationId
      const labTestArray = Array.isArray(data) ? data : [];
      setLabTestListFromBase(labTestArray);
    } catch (error) {
      console.error("Error fetching lab tests from base table:", error);
      setLabTestListFromBase([]); // Set empty array on error
    }
  };

  const fetchLabTestList = async () => {
    try {
      const response = await getLabtestsList();
      if (Array.isArray(response)) {
        // Filter by organization to ensure only organization-specific lab tests are shown
        const filteredLabTests = filterByOrganization(response, user);
        setLabTestList(filteredLabTests);
      } else {
        setLabTestList([]);
      }
    } catch (error) {
      console.error("Failed to fetch lab tests", error);
      setLabTestList([]);
    }
  };

  const fetchPatientDetails = async () => {
    try {
      const res = await getPatientById(patientId);
      setPatientInfo(res);
    } catch (error) {
      console.error("Failed to fetch patient details", error);
    }
  };

  const fetchAdmissionDetails = async () => {
    try {
      if (admissionId == null || admissionId === "null") return;
      const res = await getById(admissionId);
      setAdmissionInfo(res);
    } catch (error) {
      console.error("Failed to fetch admission", error);
    }
  };

 const fetchVitalSigns = async () => {
  try {
    const res = await getByPatientAndAdmission(
      patient_info.id,
      admissionId
    );

    console.log("Vital API:", res); // ✅ FIXED

    setVitalList((prev) => ({
      ...prev,
      [admissionId]: res, // ✅ FIXED
    }));
  } catch (err) {
    console.error("Fetch vital error:", err);
  }
};
  const fetchAssignmentDetails = async () => {
    try {
      if (admissionId == null || admissionId === "null") return;
      const res = await getAssignmentByAdmissionId(admissionId);
      const normalized = Array.isArray(res) ? res : (res ? [res] : []);
      // Filter by organization to ensure only organization-specific assignments are shown
      const filteredAssignments = filterByOrganization(normalized, user);
      
      // Fetch lookup data for enriching assignments
      let currentStaffLookup, currentWardLookup, currentBedLookup;
      try {
        const [staff, wards, beds] = await Promise.all([
          getAllStaff().catch(() => []),
          getAllWards().catch(() => []),
          getAllBeds().catch(() => []),
        ]);

        // Filter by organization
        const filteredStaff = filterByOrganization(staff || [], user);
        const filteredWards = filterByOrganization(wards || [], user);
        const filteredBeds = filterByOrganization(beds || [], user);

        // Build staff lookup map
        currentStaffLookup = new Map();
        if (Array.isArray(filteredStaff) && filteredStaff.length) {
          filteredStaff.forEach((s) => {
            if (s && s.id != null) {
              const staffName = s.name || s.fullName || s.username || "";
              currentStaffLookup.set(String(s.id), staffName);
              currentStaffLookup.set(Number(s.id), staffName);
              if (s.userId != null) {
                currentStaffLookup.set(String(s.userId), staffName);
                currentStaffLookup.set(Number(s.userId), staffName);
              }
            }
          });
        }

        // Build ward lookup map
        currentWardLookup = new Map();
        if (Array.isArray(filteredWards) && filteredWards.length) {
          filteredWards.forEach((w) => {
            if (w && w.id != null) {
              const wardName = w.name || w.wardName || w.ward || "";
              currentWardLookup.set(Number(w.id), wardName);
              currentWardLookup.set(String(w.id), wardName);
            }
          });
        }

        // Build bed lookup map
        currentBedLookup = new Map();
        if (Array.isArray(filteredBeds) && filteredBeds.length) {
          filteredBeds.forEach((b) => {
            if (b && b.id != null) {
              const bedName = b.bedNumber || b.name || b.bed || "";
              currentBedLookup.set(Number(b.id), bedName);
              currentBedLookup.set(String(b.id), bedName);
            }
          });
        }
      } catch (lookupError) {
        console.error("Failed to fetch lookup data:", lookupError);
        currentStaffLookup = new Map();
        currentWardLookup = new Map();
        currentBedLookup = new Map();
      }
      
      // Enrich assignments with names from lookups
      const enrichedAssignments = filteredAssignments.map(assignment => {
        const enriched = { ...assignment };
        
        // Fetch doctor name if needed
        if (enriched.doctorId) {
          // Will be fetched separately below
        }
        
        // Enrich wardName from wardId
        if (enriched.wardId != null && currentWardLookup.size > 0) {
          const wardName = currentWardLookup.get(Number(enriched.wardId)) || 
                          currentWardLookup.get(String(enriched.wardId));
          if (wardName) {
            enriched.wardName = wardName;
          }
        }
        
        // Enrich bedName from bedId
        if (enriched.bedId != null && currentBedLookup.size > 0) {
          const bedName = currentBedLookup.get(Number(enriched.bedId)) || 
                         currentBedLookup.get(String(enriched.bedId));
          if (bedName) {
            enriched.bedName = bedName;
          }
        }
        
        return enriched;
      });
      
      setAssignmentInfo(enrichedAssignments);
      
      // Store assignment details for medicine management
      if (enrichedAssignments.length > 0) {
        const assignment = enrichedAssignments[0];
        // Fetch doctor name if needed
        if (assignment.doctorId) {
          try {
            const doctor = await getDoctorById(assignment.doctorId);
            if (doctor && doctor.name) {
              assignment.doctorName = doctor.name;
            }
          } catch (error) {
            console.error("Failed to fetch doctor:", error);
          }
        }
        setAssignmentDetails(assignment);
      }
    } catch (error) {
      console.error("Failed to fetch assignment", error);
    }
  };

  // Fetch current staff ID if user is staff/nurse
  const fetchCurrentStaffId = async () => {
    if (user && (user.roleName === "staff" || user.roleName === "nurse")) {
      try {
        const staffDetails = await getStaffByUserId(user.id);
        setCurrentStaffId(staffDetails.id);
      } catch (error) {
        console.error("Failed to fetch staff ID:", error);
      }
    }
  };

  // Fetch medicine management data
  const fetchMedicineManagementData = async () => {
    if (!admissionId) return;
    
    try {
      // Fetch treatments
      const treatments = await getTreatmentsByAdmissionId(admissionId);
      const treatmentsArray = Array.isArray(treatments) ? treatments : [];
      // Filter by organization to ensure only organization-specific treatments are shown
      const filteredTreatments = filterByOrganization(treatmentsArray, user);
      
      // Fetch staff lookup to enrich performedByName
      let currentStaffLookup = new Map();
      try {
        const staff = await getAllStaff().catch(() => []);
        const filteredStaff = filterByOrganization(staff || [], user);
        
        // Build staff lookup map
        if (Array.isArray(filteredStaff) && filteredStaff.length) {
          filteredStaff.forEach((s) => {
            if (s && s.id != null) {
              const staffName = s.name || s.fullName || s.username || "";
              currentStaffLookup.set(String(s.id), staffName);
              currentStaffLookup.set(Number(s.id), staffName);
              if (s.userId != null) {
                currentStaffLookup.set(String(s.userId), staffName);
                currentStaffLookup.set(Number(s.userId), staffName);
              }
            }
          });
        }
      } catch (lookupError) {
        console.error("Failed to fetch staff lookup:", lookupError);
      }
      
      // Enrich treatments with performedByName from staff lookup
      const enrichedTreatments = filteredTreatments.map(treatment => {
        const enriched = { ...treatment };
        
        // Enrich performedByName from performedBy (staff ID)
        if (enriched.performedBy && currentStaffLookup.size > 0) {
          const staffId = String(enriched.performedBy);
          const staffName = currentStaffLookup.get(staffId) || currentStaffLookup.get(Number(enriched.performedBy));
          if (staffName) {
            enriched.performedByName = staffName;
          } else if (!enriched.performedByName) {
            // If lookup failed but we have performedBy, at least set something
            enriched.performedByName = String(enriched.performedBy);
          }
        } else if (!enriched.performedByName && enriched.performedBy) {
          // If lookup is empty but we have performedBy, at least set something
          enriched.performedByName = String(enriched.performedBy);
        }
        
        return enriched;
      });
      
      setTreatmentList(enrichedTreatments);
      console.log("Treatment List 👉", enrichedTreatments);

      // Fetch medicine requests
      try {
        const requests = await getRequestsByAdmissionId(admissionId);
        const requestsArray = Array.isArray(requests) ? requests : [];
        // Filter by organization to ensure only organization-specific requests are shown
        const filteredRequests = filterByOrganization(requestsArray, user);
        const requestsMap = {};
        // Map requests by both treatmentId and patientInventoryId/medicineInventoryId (if available)
        // This allows multiple inventory items for the same treatment to have independent request statuses
        filteredRequests.forEach(request => {
          if (request.treatmentId) {
            // Use patientInventoryId if available, otherwise use medicineInventoryId
            const inventoryId = request.patientInventoryId || request.medicineInventoryId;
            
            // Create a composite key: treatmentId-inventoryId (if inventoryId exists)
            // This ensures each inventory item has its own request status
            const key = inventoryId 
              ? `${request.treatmentId}-${inventoryId}`
              : request.treatmentId;
            requestsMap[key] = request;
            
            // Also store by treatmentId for lookup when no inventory item exists yet
            // If multiple requests exist for the same treatment, keep the most recent one (by ID or date)
            if (!requestsMap[request.treatmentId]) {
              requestsMap[request.treatmentId] = request;
            } else {
              // If a request already exists for this treatmentId, keep the one with the higher ID (more recent)
              const existingRequest = requestsMap[request.treatmentId];
              if (request.id > existingRequest.id) {
                requestsMap[request.treatmentId] = request;
              }
            }
          }
        });
        setMedicineRequests(requestsMap);
      } catch (error) {
        console.error("Failed to fetch medicine requests:", error);
        setMedicineRequests({});
      }

      // Fetch patient medicine inventory
      try {
        const inventory = await getPatientMedicineInventory(admissionId);
        const inventoryArray = Array.isArray(inventory) ? inventory : [];
        // Filter by organization to ensure only organization-specific inventory items are shown
        const filteredInventory = filterByOrganization(inventoryArray, user);
        setPatientMedicineInventory(filteredInventory);
      } catch (error) {
        console.error("Failed to fetch patient medicine inventory:", error);
        setPatientMedicineInventory([]);
      }
    } catch (error) {
      console.error("Failed to fetch medicine management data:", error);
    }
  };

  const handleAddVitalSigns = () => {
    const updatedList = [
      ...formik.values.vitalSignsList,
      {
        bp: "",
        temperature: "",
        heartRate: "",
        respiratoryRate: "",
        spo2: "",
        painScore: "",
        gcs: "",
      },
    ];
    formik.setFieldValue("vitalSignsList", updatedList);
  };

  // Formik Data saving values and topics 
  const formik = useFormik({
    initialValues: {
      patientId:"",
      admissionId:"",
      nurseId:"",
      pastMedicalHistory: "",
      pastSurgicalHistory: "",
      allergies: "",
      fallOrSeizureRisk: "",
      infectionPrecautions: "",
      mobilityLevel: "",
      turningSchedule: "",
      fallRiskPrecautions: "",
      observations: "",
      shift: "",
      observationsInterventions: "",
      educationProvided: "",
      familyCounseling: "",
      specialNotes: "",
      dnr: "",
      dischargePlans: "",

      vitalSignsList: [
        {
          bp: "",
          temperature: "",
          heartRate: "",
          respiratoryRate: "",
          spo2: "",
          painScore: "",
          gcsScore: "", 
           dateTime: "", // ✅ MUST EXIST
        },
      ],
      dietPlanList: [ 
        {
          dietType: "",
          feedingMethod: "",
          fluidRestriction: "",
        },
      ],
      eliminationList: [
        {
          urine: "",
          stool: "",
          vomitingOrDrains: "",
        },
      ],
      investigationsList: [
        {
          testName: "",
          status: "",
          importantResults: "",
        },
      ],
      nursingCareList: [
        {
          problem: "",
          intervention: "",
          goal: "",
        },
      ],
      deviceProceduresList: [
        {
          ivLines: "",
          catheters: "",
          tubes: "",
          drainSites: "",
        },
      ],
      medicationsList: [
        {
          medicationName: "",
          dose: "",
          route: "",
          timeGiven: "",
        },
      ],
      ivFluidsList: [
        {
          fluidType: "",
          rate: "",
          site: "",
          startTime: "",
        },
      ],
    },
    enableReinitialize: true,
    validationSchema: Yup.lazy((values) => {
      switch (values.sectionName) {
        case "Medical History":
          return medicalHistoryValidationSchema;
        case "Vital Signs Monitoring":
          return vitalSignsValidationSchema;
        case "Nutrition / Diet Plan":
          return dietPlanValidationSchema;
        case "Elimination / Output":
          return eliminationValidationSchema;
        case "Investigations":
          return investigationsValidationSchema;
        case "Nursing Care Plan":
          return nursingCareValidationSchema;
        case "Devices / Procedures":
          return deviceProceduresValidationSchema;
        case "Activity / Mobility":
          return mobilityValidationSchema;
        case "Shift-wise Nursing Notes":
          return shiftNotesValidationSchema;
        case "Patient Education & Communication":
          return educationCommunicationValidationSchema;
        case "Miscellaneous / Alerts":
          return miscellaneousValidationSchema;
        case "Medications":
          return medicationsValidationSchema;
        case "IV Fluids":
          return ivFluidsValidationSchema;
        default:
          return Yup.object();
      }
    }),
    onSubmit: async (values) => {
    if (values.sectionName === "Medical History") {
      const payload = {
        patientId: patient_info.id,
        admissionId: admissionId,
        nurseId: user.id,
        pastMedicalHistory: values.pastMedicalHistory,
        pastSurgicalHistory: values.pastSurgicalHistory,
        allergies: values.allergies,
        fallOrSeizureRisk: values.fallOrSeizureRisk,
        infectionPrecautions: values.infectionPrecautions,
      };
      await create(payload);
      alert('Medical History added successfully!')
    }

    if (values.sectionName === "Vital Signs Monitoring") {
      const payload = formik.values.vitalSignsList.map(item => ({
        patientId: patient_info.id,
        admissionId: admissionId,
        nurseId: user.id,
        bp: item.bp,
        temperature: item.temperature,
        heartRate: item.heartRate,
        respiratoryRate: item.respiratoryRate,
        spo2: item.spo2,
        painScore: item.painScore,
        gcsScore: item.gcsScore
      }));
        console.log("Payload:", payload);

      await createVitalSigns(payload);
      await fetchVitalSigns(); // ✅ refresh table
     
      alert("Vital Signs saved successfully !");
    }

    if (values.sectionName === "Nutrition / Diet Plan") {
      const payload = values.dietPlanList.map((item) => ({
        patientId: patient_info.id,
        admissionId: admissionId,
        nurseId: user.id,
        dietType: item.dietType,
        feedingMethod: item.feedingMethod,
        fluidRestriction: item.fluidRestriction,
      }));
      await createDietPlans(payload);
      alert("Nutrition / Diet Plan saved successfully!");
    }

    if (values.sectionName === "Elimination / Output") {
      const payload = values.eliminationList.map(item => ({
        patientId: patient_info.id,
        admissionId: admissionId,
        nurseId: user.id,
        urine: item.urine,
        stool: item.stool,
        vomitingOrDrains: item.vomitingOrDrains,
      }));
      await createElimination(payload); // your API function
      alert("Elimination records saved successfully!");
    }

    if (values.sectionName === "Investigations") {
      const payload = values.investigationsList.map(item => ({
        patientId: patient_info.id,
        admissionId: admissionId,
        nurseId: user.id,
        testName: item.testName,
        status: item.status,
        importantResults: item.importantResults,
      }));

      await createInvestigations(payload); 
      alert("Investigations saved successfully!");
    }

    if (values.sectionName === "Nursing Care Plan") {
      const payload = values.nursingCareList.map(item => ({
        patientId: patient_info.id,
        admissionId: admissionId,
        nurseId: user.id,
        problem: item.problem,
        intervention: item.intervention,
        goal: item.goal,
      }));
      await createCare(payload); 
      alert("Nursing Care Plan saved successfully!");
    }

    if (values.sectionName === "Devices / Procedures") {
      const payload = values.deviceProceduresList.map(item => ({
        patientId: patient_info.id,
        admissionId: admissionId,
        nurseId: user.id,
        ivLines: item.ivLines,
        catheters: item.catheters,
        tubes: item.tubes,
        drainSites: item.drainSites,
      }));
      await createProcedures(payload); 
      alert("Devices / Procedures saved successfully!");
    }

    if (values.sectionName === "Activity / Mobility") {
      const payload = {
        patientId: patient_info.id,
        admissionId: admissionId,
        nurseId: user.id,
        mobilityLevel: values.mobilityLevel,
        turningSchedule: values.turningSchedule,
        fallRiskPrecautions: values.fallRiskPrecautions,
      };
      await createMobility(payload);
      alert("Mobility data saved successfully!");
    }

    if (values.sectionName === "Shift-wise Nursing Notes") {
      const payload = {
        patientId: patient_info.id,
        admissionId: admissionId,
        nurseId: user.id,
        shift:values.shift,
        observationsInterventions: values.observationsInterventions,
      };
      await createShiftNotes(payload);
      alert("Shift-wise nursing notes saved successfully!");
    }

    if (values.sectionName === "Patient Education & Communication") {
      const payload = {
        patientId: patient_info.id,
        admissionId: admissionId,
        nurseId: user.id,
        educationProvided: values.educationProvided,
        familyCounseling: values.familyCounseling,
      };
      await createCommunication(payload);
      alert("Education / Communication info saved successfully!");
    }

    if (values.sectionName === "Miscellaneous / Alerts") {
      const payload = {
        patientId: patient_info.id,
        admissionId: admissionId,
        nurseId: user.id,
        specialNotes: values.specialNotes,
        dnr: values.dnr,
        dischargePlans: values.dischargePlans,
      };
      await createMisc(payload);
      alert("Miscellaneous / Alerts data saved successfully!");
    }

    if (values.sectionName === "Medications") {
      const payload = values.medicationsList.map((item) => ({
        patientId: patient_info.id,
        admissionId: admissionId,
        nurseId: user.id,
        medicationName: item.medicationName,
        dose: item.dose,
        route: item.route,
        timeGiven: item.timeGiven,
        treatmentType: "Medicine"
      }));
      await createMedicationsWithoutInventoryDeduction(payload);
      alert("Medications saved successfully!");   
    }

    if (values.sectionName === "IV Fluids") {
      const payload = values.ivFluidsList.map(item => ({
        patientId: patient_info.id,
        admissionId: admissionId,
        nurseId: user.id,
        fluidType: item.fluidType,
        rate: item.rate,
        site: item.site,
        startTime: item.startTime,
        treatmentType: "IV Fluids"
      }));

      try {
        await createIVFluids(payload); 
        alert("IV Fluids saved successfully!");
      } catch (error) {
        console.error("Error saving IV Fluids:", error);
        alert("Failed to save IV Fluids.");
      }
    }
    onClose();
  },
  });

  const handleRemoveVitalSigns = (index) => {
    const updatedList = formik.values.vitalSignsList.filter((_, i) => i !== index);
    formik.setFieldValue("vitalSignsList", updatedList);
  };

  // Medication 
  const [medicationsList, setMedicationsList] = useState([
    { name: "", dose: "", route: "", time: "" }
  ]);

  const handleAddMedication = () => {
    const newList = [
      ...formik.values.medicationsList,
      { medicationName: "", dose: "", route: "", timeGiven: "" },
    ];
    formik.setFieldValue("medicationsList", newList);
  };

  const handleRemoveMedication = (index) => {
    const updatedList = [...formik.values.medicationsList];
    updatedList.splice(index, 1);
    formik.setFieldValue("medicationsList", updatedList);
  };

  const handleMedicationChange = (index, field, value) => {
    const updatedList = [...medicationsList];
    updatedList[index][field] = value;
    setMedicationsList(updatedList);
  };

  // Medicine Request Handlers
  const handleRequestMedicine = (treatment) => {
    setSelectedRequestTreatment(treatment);
    setOpenRequestModal(true);
  };

  const handleRequestModalClose = () => {
    setOpenRequestModal(false);
    setSelectedRequestTreatment(null);
  };

  const handleRequestSubmit = async () => {
    await fetchMedicineManagementData();
    setOpenRequestModal(false);
    setSelectedRequestTreatment(null);
  };

  const handleReceiveMedicine = async (treatment, inventoryItem = null) => {
    try {
      // Look up request - use improved lookup logic
      let request = null;
      
      if (inventoryItem?.id) {
        // Try composite key first
        const requestKey = `${treatment.id}-${inventoryItem.id}`;
        request = medicineRequests[requestKey];
      }
      
      // If not found, try by treatmentId
      if (!request) {
        request = medicineRequests[treatment.id];
      }
      
      // If still not found, search all requests for this treatmentId
      if (!request) {
        const allRequests = Object.values(medicineRequests);
        request = allRequests.find(req => req.treatmentId === treatment.id) || null;
      }
      
      if (request) {
        const receiveData = {
          receivedByUserId: user?.id || 0,
          receivedByName: user?.name || user?.username || "Unknown",
          receivedDate: new Date().toISOString(),
        };
        await receiveMedicineRequest(request.id, receiveData);
        await fetchMedicineManagementData();
        alert("Medicine received successfully!");
      } else {
        console.error("Request not found for treatment:", treatment.id);
        alert("Error: Could not find medicine request. Please try again.");
      }
    } catch (error) {
      console.error("Error receiving medicine:", error);
      alert("Failed to mark medicine as received. Please try again.");
    }
  };

  const handleDispenseToPatient = async (treatment, inventoryItem = null) => {
    try {
      // Look up request using improved lookup logic
      let request = null;
      
      if (inventoryItem?.id) {
        // Try composite key first
        const requestKey = `${treatment.id}-${inventoryItem.id}`;
        request = medicineRequests[requestKey];
      }
      
      // If not found, try by treatmentId
      if (!request) {
        request = medicineRequests[treatment.id];
      }
      
      // If still not found, search all requests for this treatmentId
      if (!request) {
        const allRequests = Object.values(medicineRequests);
        request = allRequests.find(req => req.treatmentId === treatment.id) || null;
      }
      
      if (request) {
        // Use provided inventoryItem or find the first matching one
        const targetInventoryItem = inventoryItem || patientMedicineInventory.find(
          item => item.medicineName === treatment.medicine
        );
        if (targetInventoryItem) {
          setSelectedInventoryItem(targetInventoryItem);
          setSelectedAdministrationRequest(request);
          setOpenAdministrationModal(true);
        } else {
          const tempInventoryItem = {
            id: `temp-${treatment.id}`,
            patientId: patient_info.id,
            admissionId: admissionId,
            medicineId: request.medicineId,
            medicineName: treatment.medicine,
            remainingQuantity: request.totalQuantity || 0,
            batchNumber: request.batchNumber || "N/A",
            expiryDate: request.expiryDate || null,
            dosage: treatment.dosage,
            issuedDate: request.issuedDate,
            issuedBy: request.issuedByName || "Pharmacy"
           
            
           
          };
          setSelectedInventoryItem(tempInventoryItem);
          setSelectedAdministrationRequest(request);
          setOpenAdministrationModal(true);
        }
      }
    } catch (error) {
      console.error("Error preparing medicine administration:", error);
      alert("Failed to prepare medicine administration. Please try again.");
    }
  };

  const handleReturnModalClose = () => {
    setOpenReturnModal(false);
    setSelectedReturnTreatment(null);
  };

  const handleReturnSubmit = async () => {
    await fetchMedicineManagementData();
    setOpenReturnModal(false);
    setSelectedReturnTreatment(null);
  };

  const handleAdministrationModalClose = () => {
    setOpenAdministrationModal(false);
    setSelectedInventoryItem(null); 
    setSelectedAdministrationRequest(null);
  };

  const handleAdministrationSubmit = async () => {
    try {
      // Use the stored request directly from when the modal was opened
      const request = selectedAdministrationRequest;
      console.log(request);
      if (!request) {
        console.error("No request found for administration");
        alert("Error: Could not find medicine request. Please try again.");
        return;
      }

      // Find the treatment using the request's treatmentId to ensure we get the correct one
      const treatment = treatmentList.find(t => t.id === request.treatmentId);
      
      if (!treatment) {
        console.error("Treatment not found for request treatmentId:", request.treatmentId);
        alert("Error: Could not find treatment. Please try again.");
        return;
      }

      // Verify the treatment matches the inventory item's medicine name
      if (treatment.medicine !== selectedInventoryItem?.medicineName) {
        console.error("Treatment medicine mismatch:", {
          treatmentMedicine: treatment.medicine,
          inventoryItemMedicine: selectedInventoryItem?.medicineName
        });
        alert("Error: Treatment does not match inventory item.");
        return;
      }
    
      

      // Update status to DISPENSED if request status is RECEIVED or ISSUED
      // (ISSUED can happen if medicine was issued but not yet received in ward)
      if (request.status === 'RECEIVED' || request.status === 'ISSUED') {
        const dispenseData = {
          dispensedByUserId: user?.id || 0,
          dispensedByName: user?.name || user?.username || "Unknown",
          dispensedDate: new Date().toISOString(),
        };
        await dispenseMedicineRequest(request.id, dispenseData);
      } else if (request.status === 'DISPENSED') {
        // If already DISPENSED, we're giving additional doses - no need to update status
        console.log("Medicine already dispensed, giving additional dose");
      } else {
        // For any other status, log a warning but don't update
        console.warn("Cannot update request status to DISPENSED. Current status:", request.status);
      }
    } catch (error) {
      console.error("Error marking medicine as dispensed:", error);
      alert("Failed to update medicine status. Please try again.");
    }
    await fetchMedicineManagementData();
    setOpenAdministrationModal(false);
    setSelectedInventoryItem(null);
    setSelectedAdministrationRequest(null);
  };

  const handleReturnPatientMedicine = async (treatment, inventoryItem, request) => {
    try {
      // Validate that we have the required parameters
      if (!treatment || !inventoryItem || !request) {
        console.error("Missing required parameters for return:", { treatment, inventoryItem, request });
        alert("Error: Missing required information to return medicine.");
        return;
      }

      // Verify that the request matches this treatment and inventory item
      if (request.treatmentId !== treatment.id) {
        console.error("Request treatmentId mismatch:", {
          requestTreatmentId: request.treatmentId,
          treatmentId: treatment.id
        });
        alert("Error: Request does not match treatment.");
        return;
      }

      // Verify that the request matches this inventory item
      // Check by patientInventoryId if available, otherwise check by treatmentId and dosage
      if (request.patientInventoryId) {
        if (request.patientInventoryId !== inventoryItem.id) {
          console.error("Request patientInventoryId mismatch:", {
            requestPatientInventoryId: request.patientInventoryId,
            inventoryItemId: inventoryItem.id
          });
          alert("Error: Request does not match inventory item.");
          return;
        }
      } else {
        // If patientInventoryId is not available, verify by treatmentId and dosage
        if (request.treatmentId !== treatment.id || 
            (request.dosage && inventoryItem.dosage && request.dosage !== inventoryItem.dosage)) {
          console.error("Request treatmentId/dosage mismatch:", {
            requestTreatmentId: request.treatmentId,
            treatmentId: treatment.id,
            requestDosage: request.dosage,
            inventoryItemDosage: inventoryItem.dosage
          });
          alert("Error: Request does not match treatment or dosage.");
          return;
        }
      }

      // Verify request status
      if (request.status !== 'RECEIVED' && request.status !== 'DISPENSED') {
        console.error("Invalid request status for return:", request.status);
        alert(`Error: Cannot return medicine. Current status is ${request.status}.`);
        return;
      }

      const returnData = {
        patientInventoryId: inventoryItem.id,
        patientId: patient_info.id,
        admissionId: admissionId,
        medicineId: inventoryItem.medicineId,
        medicineName: inventoryItem.medicineName,
        quantity: inventoryItem.remainingQuantity,
        returnReason: "Unused medicine - order changed or patient discharged",
        returnedBy: user?.name || user?.username || "Unknown",
        returnedDate: new Date().toISOString(),
      };
      await returnPatientMedicineToPharmacy(returnData);

      // Fetch doctorName if not available in request (for IPD requests)
      // Backend needs doctorName for transaction record in {orgId}_medicine_transactions
      let doctorName = request?.doctorName || null;
      if (!doctorName && admissionId) {
        try {
          // Fetch assignment to get doctorId
          const assignments = await getAssignmentByAdmissionId(admissionId);
          const assignment = Array.isArray(assignments) ? assignments[0] : assignments;
          
          if (assignment?.doctorId) {
            // Fetch doctor name using doctorId
            const doctor = await getDoctorById(assignment.doctorId);
            if (doctor?.name) {
              doctorName = doctor.name;
            }
          }
        } catch (error) {
          console.warn("Failed to fetch doctor name for return transaction:", error);
          // Continue without doctorName - backend will handle gracefully
        }
      }

      // Use the request object passed from the table row directly
      // Include all necessary fields for backend to create transaction in {orgId}_medicine_transactions
      // Backend will fetch patientName from organization-specific patient table
      const returnRequestData = {
        returnedByUserId: user?.id || 0,
        returnedByName: user?.name || user?.username || "Unknown",
        returnedDate: new Date().toISOString(),
        returnReason: "Unused medicine returned to pharmacy",
        returnedQuantity: inventoryItem.remainingQuantity,
        // Additional fields for transaction creation
        patientId: request.patientId || patient_info?.id || null,
        admissionId: request.admissionId || admissionId || null,
        medicineId: request.medicineId || inventoryItem.medicineId || null,
        medicineName: request.medicineName || inventoryItem.medicineName || null,
        dosage: request.dosage || inventoryItem.dosage || null,
        quantity: inventoryItem.remainingQuantity,
        patientType: (request.admissionId || admissionId) ? "ipd" : "opd",
        transactionType: "RETURN",
        doctorName: doctorName, // ✅ Fetched from assignment if not in request
      };
      await returnMedicineRequest(request.id, returnRequestData);
      
      await fetchMedicineManagementData();
      alert("Medicine returned to pharmacy successfully! Status updated to RETURNED.");
    } catch (error) {
      console.error("Error returning patient medicine:", error);
      alert("Failed to return medicine to pharmacy. Please try again.");
    }
  };

  // IV Fluids 
  const [ivFluidsList, setIVFluidsList] = useState([
    { fluidType: "", rate: "", site: "", startTime: "" }
  ]);

  const handleAddIVFluid = () => {
    formik.setFieldValue("ivFluidsList", [
      ...formik.values.ivFluidsList,
      { fluidType: "", rate: "", site: "", startTime: "" },
    ]);
  };

  const handleRemoveIVFluid = (index) => {
    const updated = [...formik.values.ivFluidsList];
    updated.splice(index, 1);
    formik.setFieldValue("ivFluidsList", updated);
  };


  // Nutrition
  const [nutritionList, setNutritionList] = useState([
    { dietType: "", feedingMethod: "", fluidRestriction: "" }
  ]);

  const handleAddNutrition = () => {
    setNutritionList([...nutritionList, { dietType: "", feedingMethod: "", fluidRestriction: "" }]);
  };

  const handleRemoveNutrition = (index) => {
    const updated = [...nutritionList];
    updated.splice(index, 1);
    setNutritionList(updated);
  };

  // Elimination 
  const [eliminationList, setEliminationList] = useState([
    { urine: "", stool: "", vomitingDrains: "" }
  ]);

  const handleAddElimination = () => {
    setEliminationList([...eliminationList, { urine: "", stool: "", vomitingDrains: "" }]);
  };

  const handleRemoveElimination = (index) => {
    const updated = [...eliminationList];
    updated.splice(index, 1);
    setEliminationList(updated);
  };

  // Investigations 
  const [investigationList, setInvestigationList] = useState([
    { testName: "", status: "", importantResults: "" }
  ]);

  const handleAddInvestigation = () => {
    setInvestigationList([...investigationList, { testName: "", status: "", importantResults: "" }]);
  };

  const handleRemoveInvestigation = (index) => {
    const updated = [...investigationList];
    updated.splice(index, 1);
    setInvestigationList(updated);
  };

  // Nursing Care
  const [nursingCareList, setNursingCareList] = useState([
    { problem: "", intervention: "", goal: "" }
  ]);

  const handleAddNursingCare = () => {
    const updatedList = [...formik.values.nursingCareList, { problem: "", intervention: "", goal: "" }];
    formik.setFieldValue("nursingCareList", updatedList);
  };

  const handleRemoveNursingCare = (index) => {
    const updatedList = [...formik.values.nursingCareList];
    updatedList.splice(index, 1);
    formik.setFieldValue("nursingCareList", updatedList);
  };

  // Devices Procedures 
  const [devicesList, setDevicesList] = useState([
    { lines: "", catheters: "", tubes: "", drains: "" }
  ]);

  const handleAddDevice = () => {
    formik.setFieldValue("deviceProceduresList", [
    ...formik.values.deviceProceduresList,
    { ivLines: "", catheters: "", tubes: "", drainSites: "" },
    ]);
  };

  const handleRemoveDevice = (index) => {
    const updated = [...devicesList];
    updated.splice(index, 1);
    setDevicesList(updated);
  };

  const formatDateTime = (dateArray, timeArray) => {
    if (!dateArray || !timeArray) return "";
    
    // Handle array format: [year, month, day] and [hour, minute, second]
    if (Array.isArray(dateArray) && Array.isArray(timeArray)) {
      const [year, month, day] = dateArray.map(v => Number(v) || 0);
      const [hour, minute, second] = timeArray.map(v => Number(v) || 0);
      
      // Validate that we have valid date values
      if (!year || year < 1000 || !month || !day) {
        // Try to parse as string if array is invalid
        if (typeof dateArray === 'string') {
          const dateStr = dateArray.split('T')[0];
          const timeStr = typeof timeArray === 'string' ? timeArray : timeArray.join(':');
          return `${dateStr} ${timeStr}`;
        }
        return "";
      }
      
      const pad = (n) => String(n).padStart(2, "0");
      return `${year}-${pad(month)}-${pad(day)} ${pad(hour)}:${pad(minute)}:${pad(second)}`;
    }
    
    // Handle string format: ISO date strings
    if (typeof dateArray === 'string') {
      const dateStr = dateArray.split('T')[0];
      let timeStr = "";
      if (typeof timeArray === 'string') {
        timeStr = timeArray;
      } else if (Array.isArray(timeArray)) {
        const [h, m, s] = timeArray.map(v => Number(v) || 0);
        timeStr = `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
      }
      return timeStr ? `${dateStr} ${timeStr}` : dateStr;
    }
    
    // Handle Date object
    if (dateArray instanceof Date) {
      const dateStr = dateArray.toISOString().split('T')[0];
      let timeStr = "";
      if (timeArray instanceof Date) {
        timeStr = timeArray.toTimeString().split(' ')[0];
      } else if (typeof timeArray === 'string') {
        timeStr = timeArray;
      } else if (Array.isArray(timeArray)) {
        const [h, m, s] = timeArray.map(v => Number(v) || 0);
        timeStr = `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
      }
      return timeStr ? `${dateStr} ${timeStr}` : dateStr;
    }
    
    return "";
  };

  const isDateTimeArray = (arr) =>
    Array.isArray(arr) && arr.length >= 6 && Number(arr[0]) > 1000;

const combineDateAndTime = (dateArray, timeValue) => {
  if (!Array.isArray(dateArray) || dateArray.length < 3 || !timeValue) return null;

  const [year, month = 1, day = 1] = dateArray.map((v) => Number(v) || 0);
  let hour = 0;
  let minute = 0;
  let second = 0;

  if (Array.isArray(timeValue)) {
    [hour = 0, minute = 0, second = 0] = timeValue.map((v) => Number(v) || 0);
  } else if (typeof timeValue === "object" && timeValue !== null) {
    hour = Number(timeValue.hour) || 0;
    minute = Number(timeValue.minute) || 0;
    second = Number(timeValue.second) || 0;
  } else if (typeof timeValue === "string" || typeof timeValue === "number") {
    const trimmed = String(timeValue).trim();
    const twelveHourMatch = trimmed.match(
      /^(\d{1,2}):(\d{2})(?::(\d{2}))?\s?(AM|PM)$/i
    );
    if (twelveHourMatch) {
      hour = Number(twelveHourMatch[1]) || 0;
      minute = Number(twelveHourMatch[2]) || 0;
      second = Number(twelveHourMatch[3] || 0) || 0;
      const meridiem = twelveHourMatch[4].toUpperCase();
      if (meridiem === "AM" && hour === 12) {
        hour = 0;
      } else if (meridiem === "PM" && hour !== 12) {
        hour += 12;
      }
    } else {
      const twentyFourHourMatch = trimmed.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?$/);
      if (twentyFourHourMatch) {
        hour = Number(twentyFourHourMatch[1]) || 0;
        minute = Number(twentyFourHourMatch[2]) || 0;
        second = Number(twentyFourHourMatch[3] || 0) || 0;
      } else {
        return null;
      }
    }
  } else {
    return null;
  }

  return [year, month, day, hour, minute, second];
};

  const parseTimeValue = (value) => {
    if (!value) return null;

    const finalize = (date) => (date && date.isValid() ? date : null);
    // Helper to build a time-only date object in local timezone
    // Uses a fixed date (1970-01-01) to avoid any date-related timezone issues
    const buildFromClock = (hour = 0, minute = 0, second = 0) => {
      const safeHour = Number(hour) || 0;
      const safeMinute = Number(minute) || 0;
      const safeSecond = Number(second) || 0;
      // Create a date object with a fixed date and the specified time
      // This ensures the hour value is used exactly as provided (0-23)
      return finalize(
        dayjs()
          .startOf('day')
          .hour(safeHour)
          .minute(safeMinute)
          .second(safeSecond)
          .millisecond(0)
      );
    };

    if (Array.isArray(value)) {
      if (isDateTimeArray(value)) {
        // Full datetime array: [year, month, day, hour, minute, second, nanoseconds?]
        // When the array has 7 elements (including nanoseconds), it's typically from API/database
        // and represents UTC time. We need to parse as UTC first, then convert to local time.
        const [year, month = 1, day = 1, hour = 0, minute = 0, second = 0, nanoseconds] = value.map((v) =>
          Number(v)
        );
        
        // If array has 7 elements (includes nanoseconds), treat as UTC from API/database
        // The presence of 7 elements indicates it's from API/database and represents UTC time
        if (value.length === 7) {
          // Parse as UTC, then convert to local time
          return finalize(
            dayjs.utc()
              .year(year)
              .month((month || 1) - 1)
              .date(day || 1)
              .hour(hour || 0)
              .minute(minute || 0)
              .second(second || 0)
              .millisecond(0)
              .local() // Convert UTC to local timezone
          );
        } else {
          // Parse as local time (backend sends 1-indexed months)
          // For arrays with 6 elements (no nanoseconds), assume local time
          return finalize(
            dayjs()
              .year(year)
              .month((month || 1) - 1)
              .date(day || 1)
              .hour(hour || 0)
              .minute(minute || 0)
              .second(second || 0)
              .millisecond(0)
          );
        }
      }

      if (value.length >= 3 && Number(value[0]) > 1000) {
        // Date-only array; cannot extract time
        return null;
      }

      // Time-only array: [hour, minute] or [hour, minute, second]
      // These are UTC times from the backend, so we need to treat them as UTC
      // Use today's date in UTC, set the time, then convert to local
      const [hour = 0, minute = 0, second = 0] = value.map(v => Number(v) || 0);
      return finalize(
        dayjs.utc()
          .startOf('day')
          .hour(hour)
          .minute(minute)
          .second(second)
          .millisecond(0)
          .local() // Convert UTC to local timezone
      );
    }

    if (value instanceof Date) {
      return finalize(dayjs(value));
    }

    if (
      typeof value === "object" &&
      value !== null &&
      ("hour" in value || "minute" in value || "second" in value)
    ) {
      const { hour = 0, minute = 0, second = 0 } = value;
      return buildFromClock(hour, minute, second);
    }

    if (typeof value === "string" || typeof value === "number") {
      const trimmed = String(value).trim();
      if (!trimmed) return null;

      // Check if it's a timestamp (large number representing milliseconds since epoch)
      // Timestamps are typically 13 digits (milliseconds) or 10 digits (seconds)
      const numValue = typeof value === "number" ? value : Number(trimmed);
      if (!isNaN(numValue) && numValue > 1000000000000) {
        // It's a timestamp in milliseconds (UTC)
        // Parse as UTC timestamp and convert to local time
        return finalize(dayjs.utc(numValue).local());
      }

      const twelveHourMatch = trimmed.match(
        /^(\d{1,2}):(\d{2})(?::(\d{2}))?\s?(AM|PM)$/i
      );
      if (twelveHourMatch) {
        let hour = Number(twelveHourMatch[1]);
        const minute = Number(twelveHourMatch[2]);
        const second = Number(twelveHourMatch[3] || 0);
        const meridiem = twelveHourMatch[4].toUpperCase();
        if (meridiem === "AM" && hour === 12) {
          hour = 0;
        } else if (meridiem === "PM" && hour !== 12) {
          hour += 12;
        }
        return buildFromClock(hour, minute, second);
      }

      const twentyFourHourMatch = trimmed.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?$/);
      if (twentyFourHourMatch) {
        const hour = Number(twentyFourHourMatch[1]);
        const minute = Number(twentyFourHourMatch[2]);
        const second = Number(twentyFourHourMatch[3] || 0);
        return buildFromClock(hour, minute, second);
      }

      // Try parsing as ISO string or other date formats
      const normalized = trimmed.replace(" ", "T").replace(/\.(\d{3})\d+/, ".$1");
      let parsed = dayjs(normalized);
      if (!parsed.isValid() && !normalized.endsWith("Z")) {
        parsed = dayjs(`${normalized}Z`);
      }
      if (!parsed.isValid()) {
        parsed = dayjs(new Date(normalized));
      }
      return finalize(parsed);
    }

    return null;
  };

  const formatTimeValue = (...values) => {
    for (const candidate of values) {
      if (!candidate) continue;
      
      // Skip date-only arrays (they don't have time information)
      if (Array.isArray(candidate) && !isDateTimeArray(candidate)) {
        if (candidate.length >= 3 && Number(candidate[0]) > 1000) {
          continue; // Date-only array, skip
        }
        // Time-only arrays will be handled by parseTimeValue
      }
      
      const parsed = parseTimeValue(candidate);
      if (parsed) {
        // Format the time directly - the hour value is already correct (local time)
        return parsed.format("hh:mm A");
      }
    }
    return "-";
  };
 
  return (
    <BasicModal 
      open={open} 
      onClose={onClose} 
      sx={LARGE_FORM_MODAL_SX}
    >
      <Box>
        <Typography sx={STANDARD_FORM_HEADING_SX}>
          Nurse Kardex
        </Typography>
        <form onSubmit={formik.handleSubmit}>
          <Box sx={{ maxHeight: selectedSection === "Medications" ? "75vh" : "60vh", overflowY: "auto", pr: 1 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormControl fullWidth error={formik.touched.sectionName && Boolean(formik.errors.sectionName)} sx={{ mt: 1 }}>
                  <InputLabel shrink>Select Section</InputLabel>
                  <Select
                    name="sectionName"
                    value={formik.values.sectionName}
                    // onChange={(e) => {
                    //   const value = e.target.value;
                    //   formik.setFieldValue("sectionName", value);
                    //   formik.setFieldTouched("sectionName", true);
                    //   setSelectedSection(value);
                    onChange={(e) => {
  const newSection = e.target.value;
  const prevSection = formik.values.sectionName;

  // 🔥 Reset only previous section fields
  if (prevSection && sectionFieldsMap[prevSection]) {
    sectionFieldsMap[prevSection].forEach((field) => {
      formik.setFieldValue(field, formik.initialValues[field] || "");
    });
  }

  // Update section
  formik.setFieldValue("sectionName", newSection);
  setSelectedSection(newSection);

  // ✅ Clear errors & touched of previous section
  if (prevSection && sectionFieldsMap[prevSection]) {
    const newErrors = { ...formik.errors };
    const newTouched = { ...formik.touched };

    sectionFieldsMap[prevSection].forEach((field) => {
      delete newErrors[field];
      delete newTouched[field];
    });

    formik.setErrors(newErrors);
    formik.setTouched(newTouched);
  }

                    }}
                    onBlur={formik.handleBlur}
                    label="Select Section"
                  >
                    {SECTION_OPTIONS.map((label, index) => (
                      <MenuItem key={index} value={label}>
                        {label}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>
                    {formik.touched.sectionName && formik.errors.sectionName
                      ? formik.errors.sectionName
                      : "Please select form type from dropdown to add logs"}
                  </FormHelperText>
                </FormControl>
              </Grid>

              {/* Patient Demographics */}
              {selectedSection === "Patient Demographics" && (
                <>
                  <Grid item xs={12}>
                    <Typography variant="h6" sx={{ mt: 2, mb: 2 }}>
                      Patient Demographics
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <TextField 
                      fullWidth label="Patient Name" 
                      name="patientName" 
                      value={patient_info.name}
                      InputProps={{ readOnly: true }} 
                      InputLabelProps={{ shrink: true }}/>
                     
                  </Grid>
                  <Grid item xs={6}>
                    <TextField 
                      fullWidth label="Age" 
                      name="ageGender" 
                      value={patient_info.age}
                      InputProps={{ readOnly: true }} 
                       InputLabelProps={{ shrink: true }}/>
                  </Grid>
                  <Grid item xs={6}>
                    <TextField fullWidth value={admission_info?.id || ""} label="Hospital ID / IPD Number" name="hospitalId" InputProps={{ readOnly: true }}  />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField 
                      fullWidth
                      type="text"
                      label="Date & Time of Admission"
                      name="admissionDateTime"
                      value={formatDateTime(admission_info.admissionDate, admission_info.admissionTime)}
                      InputLabelProps={{ shrink: true }}
                      InputProps={{ readOnly: true }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      value={
                        assignment_info.length > 0
                          ? assignment_info
                              .map((item) => item.doctorName || "N/A")
                              .join(", ")
                          : ""
                        }
                      label="Consultant Name"
                      name="consultantName"
                      InputProps={{ readOnly: true }}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField 
                      fullWidth  
                        value={
                          assignment_info.length > 0
                            ? assignment_info
                                .map(
                                  (item) =>
                                    `${item.wardName || "N/A"} / ${item.bedName || "N/A"}`
                                )
                                .join(", ")
                            : ""
                        } label="Ward / Bed / Room Number" name="wardBedRoom" InputProps={{ readOnly: true }} InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      value={
                        (patient_info && (patient_info.diagnosis || patient_info.disease_diagnose || patient_info.diseaseDiagnose))
                          || (admission_info && (admission_info.diagnosis || admission_info.disease_diagnose || admission_info.diseaseDiagnose))
                          || ""
                      }
                      label="Diagnosis"
                      name="diagnosis"
                      InputProps={{ readOnly: true }}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField fullWidth value={`DNR`} label="Code Status (DNR/Full Code)" name="codeStatus" InputProps={{ readOnly: true }} />
                  </Grid>
                </>
              )}

              {selectedSection === "Medical History" && (
                <>
                  <Grid item xs={12}>
                    <Typography variant="h6" sx={{ mt: 2, mb: 2 }}>
                      Medical History
                    </Typography>
                  </Grid>

                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      label="Past Medical History"
                      {...formik.getFieldProps("pastMedicalHistory")}
                      error={formik.submitCount > 0 && Boolean(formik.errors.pastMedicalHistory)}
                      helperText={formik.submitCount > 0 && formik.errors.pastMedicalHistory}
                    />
                  </Grid>

                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      label="Past Surgical History"
                      {...formik.getFieldProps("pastSurgicalHistory")}
                      error={formik.submitCount > 0 && Boolean(formik.errors.pastSurgicalHistory)}
                      helperText={formik.submitCount > 0 && formik.errors.pastSurgicalHistory}
                    />
                  </Grid>

                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      label="Allergies"
                      {...formik.getFieldProps("allergies")}
                      error={formik.submitCount > 0 && Boolean(formik.errors.allergies)}
                      helperText={formik.submitCount > 0 && formik.errors.allergies}
                    />
                  </Grid>

                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      label="Fall Risk / Seizure Risk"
                      {...formik.getFieldProps("fallOrSeizureRisk")}
                      error={formik.submitCount > 0 && Boolean(formik.errors.fallOrSeizureRisk)}
                      helperText={formik.submitCount > 0 && formik.errors.fallOrSeizureRisk}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Infection Precautions"
                      {...formik.getFieldProps("infectionPrecautions")}
                      error={formik.submitCount > 0 && Boolean(formik.errors.infectionPrecautions)}
                      helperText={formik.submitCount > 0 && formik.errors.infectionPrecautions}
                    />
                  </Grid>
                </>
              )}

              {/* Vital Signs */}
              {selectedSection === "Vital Signs Monitoring" && (
                <>
                  <Grid item xs={12}>
                    <Typography variant="h6" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
                      Vital Signs Monitoring
                    </Typography>
                  </Grid>

                  {formik.values.vitalSignsList.map((vitalSigns, index) => (
                    <React.Fragment key={index}>
                      <Grid item xs={12} sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>
                          Entry #{index + 1}
                        </Typography>
                        {/* <Button
                          variant="outlined"
                          color="error"
                          onClick={() => handleRemoveVitalSigns(index)}
                          size="small"
                        >
                          Remove
                        </Button> */}
                      </Grid>

                      <Grid item xs={2}>
                        <TextField
                          fullWidth
                          label="BP"
                          name={`vitalSignsList[${index}].bp`}
                          value={formik.values.vitalSignsList[index].bp}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          error={formik.touched.vitalSignsList?.[index]?.bp && Boolean(formik.errors.vitalSignsList?.[index]?.bp)}
                          helperText={formik.touched.vitalSignsList?.[index]?.bp && formik.errors.vitalSignsList?.[index]?.bp}
                        />
                      </Grid>

                      <Grid item xs={2}>
                        <TextField
                          fullWidth
                          label="Temperature"
                          name={`vitalSignsList[${index}].temperature`}
                          value={formik.values.vitalSignsList[index].temperature}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          error={formik.touched.vitalSignsList?.[index]?.temperature && Boolean(formik.errors.vitalSignsList?.[index]?.temperature)}
                          helperText={formik.touched.vitalSignsList?.[index]?.temperature && formik.errors.vitalSignsList?.[index]?.temperature}
                        />
                      </Grid>

                      <Grid item xs={2}>
                        <TextField
                          fullWidth
                          label="Heart Rate"
                          name={`vitalSignsList[${index}].heartRate`}
                          value={formik.values.vitalSignsList[index].heartRate}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          error={formik.touched.vitalSignsList?.[index]?.heartRate && Boolean(formik.errors.vitalSignsList?.[index]?.heartRate)}
                          helperText={formik.touched.vitalSignsList?.[index]?.heartRate && formik.errors.vitalSignsList?.[index]?.heartRate}
                        />
                      </Grid>

                      <Grid item xs={2}>
                        <TextField
                          fullWidth
                          label="Respiratory Rate"
                          name={`vitalSignsList[${index}].respiratoryRate`}
                          value={formik.values.vitalSignsList[index].respiratoryRate}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          error={formik.touched.vitalSignsList?.[index]?.respiratoryRate && Boolean(formik.errors.vitalSignsList?.[index]?.respiratoryRate)}
                          helperText={formik.touched.vitalSignsList?.[index]?.respiratoryRate && formik.errors.vitalSignsList?.[index]?.respiratoryRate}
                        />
                      </Grid>

                      <Grid item xs={2}>
                        <TextField
                          fullWidth
                          label="SpO₂"
                          name={`vitalSignsList[${index}].spo2`}
                          value={formik.values.vitalSignsList[index].spo2}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          error={formik.touched.vitalSignsList?.[index]?.spo2 && Boolean(formik.errors.vitalSignsList?.[index]?.spo2)}
                          helperText={formik.touched.vitalSignsList?.[index]?.spo2 && formik.errors.vitalSignsList?.[index]?.spo2}
                        />
                      </Grid>

                      <Grid item xs={2}>
                        <TextField
                          fullWidth
                          label="Pain Score"
                          name={`vitalSignsList[${index}].painScore`}
                          value={formik.values.vitalSignsList[index].painScore}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          error={formik.touched.vitalSignsList?.[index]?.painScore && Boolean(formik.errors.vitalSignsList?.[index]?.painScore)}
                          helperText={formik.touched.vitalSignsList?.[index]?.painScore && formik.errors.vitalSignsList?.[index]?.painScore}
                        />
                      </Grid>

                      <Grid item xs={2}>
                        <TextField
                          fullWidth
                          label="GCS (if applicable)"
                          name={`vitalSignsList[${index}].gcsScore`}
                          value={formik.values.vitalSignsList[index].gcsScore}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          error={formik.touched.vitalSignsList?.[index]?.gcsScore && Boolean(formik.errors.vitalSignsList?.[index]?.gcsScore)}
                          helperText={formik.touched.vitalSignsList?.[index]?.gcsScore && formik.errors.vitalSignsList?.[index]?.gcsScore}
                        />
                      </Grid>

                 
                      
                    </React.Fragment>
                  ))}

                  <Grid item xs={12} sx={{ mt: 2 }}>
                    <Button variant="outlined" onClick={handleAddVitalSigns}>
                      + Add More
                    </Button>
                  </Grid>
                </>
              )}

              {/* Medications - Medicine Management Table */}
              {selectedSection === "Medications" && (
                <>
                  <Grid item xs={12}>
                    {/* <Typography variant="h6" sx={{ mt: 4, mb: 2, fontWeight: 600, color: "primary.main" }}>
                      Medicine Management
                    </Typography> */}
                  </Grid>
                  <Grid item xs={12}>
                    <Box sx={{ 
                      overflowX: "auto",
                      overflowY: "auto",
                      maxHeight: "400px",
                      border: "1px solid #e0e0e0",
                      borderRadius: 1
                    }}>
                      <Table size="small" stickyHeader>
                        <TableHead>
                          <TableRow sx={{ backgroundColor: "primary.main" }}>
                            <TableCell sx={{ color: "#fff", position: "sticky", top: 0, zIndex: 1, backgroundColor: "primary.main" }}>Medicine</TableCell>
                            <TableCell sx={{ color: "#fff", position: "sticky", top: 0, zIndex: 1, backgroundColor: "primary.main" }}>Dosage</TableCell>
                            <TableCell sx={{ color: "#fff", position: "sticky", top: 0, zIndex: 1, backgroundColor: "primary.main" }}>Duration</TableCell>
                            <TableCell sx={{ color: "#fff", position: "sticky", top: 0, zIndex: 1, backgroundColor: "primary.main" }}>Prescribed By</TableCell>
                            <TableCell sx={{ color: "#fff", position: "sticky", top: 0, zIndex: 1, backgroundColor: "primary.main" }}>Performed By</TableCell>
                            <TableCell sx={{ color: "#fff", position: "sticky", top: 0, zIndex: 1, backgroundColor: "primary.main" }}>Prescribed Date</TableCell>
                            <TableCell sx={{ color: "#fff", position: "sticky", top: 0, zIndex: 1, backgroundColor: "primary.main" }}>Prescribed Time</TableCell>
                            {/* <TableCell sx={{ color: "#fff", position: "sticky", top: 0, zIndex: 1, backgroundColor: "primary.main" }}>Quantity</TableCell> */}
                            <TableCell sx={{ color: "#fff", position: "sticky", top: 0, zIndex: 1, backgroundColor: "primary.main" }}>Ward Stock</TableCell>
                            <TableCell sx={{ color: "#fff", position: "sticky", top: 0, zIndex: 1, backgroundColor: "primary.main" }}>Batch/Expiry</TableCell>
                            <TableCell sx={{ color: "#fff", position: "sticky", top: 0, zIndex: 1, backgroundColor: "primary.main" }}>Status</TableCell>
                            <TableCell sx={{ color: "#fff", position: "sticky", top: 0, zIndex: 1, backgroundColor: "primary.main" }}>Actions</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {(() => {
                            // const treatments = treatmentList || [];
                            const treatments = medicationTreatments || [];

                            const inventoryItems = patientMedicineInventory || [];
                            
                            // Create a map of inventory items by ID for quick lookup
                            const inventoryById = {};
                            inventoryItems.forEach(item => {
                              inventoryById[item.id] = item;
                            });
                            
                            // Create a reverse map: requests by patientInventoryId or by treatmentId+dosage
                            // This helps us find which inventory items are actually linked to requests
                            const requestsByInventoryId = {};
                            const requestsByTreatmentAndDosage = {}; // Map: "treatmentId-dosage" -> request
                            
                            Object.values(medicineRequests).forEach(request => {
                              // Map by patientInventoryId if available
                              if (request.patientInventoryId) {
                                requestsByInventoryId[request.patientInventoryId] = request;
                              }
                              // Also map by treatmentId and dosage for matching when patientInventoryId is not available
                              if (request.treatmentId && request.dosage) {
                                const key = `${request.treatmentId}-${request.dosage}`;
                                requestsByTreatmentAndDosage[key] = request;
                              }
                            });
                            
                            // Create a map of inventory items by medicine name
                            // Match items to requests by patientInventoryId or by treatmentId+dosage
                            // Store the linked request with each inventory item for later use
                            const inventoryByMedicine = {};
                            const inventoryItemRequestMap = new Map(); // Map: inventoryItemId -> request
                            
                            inventoryItems.forEach(item => {
                              let linkedRequest = null;
                              
                              // First try to find by patientInventoryId
                              if (requestsByInventoryId[item.id]) {
                                linkedRequest = requestsByInventoryId[item.id];
                              } else {
                                // If not found, try to match by treatmentId and dosage
                                // Find ALL treatments that match this inventory item by medicine and dosage
                                const matchingTreatments = treatments.filter(t => 
                                  t.medicine === item.medicineName && 
                                  t.dosage === item.dosage
                                );
                                
                                // For each matching treatment, check if there's a request
                                // Prefer the one that's explicitly linked
                                for (const treatment of matchingTreatments) {
                                  const key = `${treatment.id}-${item.dosage}`;
                                  const requestByDosage = requestsByTreatmentAndDosage[key];
                                  if (requestByDosage && requestByDosage.treatmentId === treatment.id) {
                                    linkedRequest = requestByDosage;
                                    break; // Use the first matching request
                                  }
                                }
                              }
                              
                              // Only include if linked to a request with status ISSUED or later
                              if (linkedRequest && ['ISSUED', 'RECEIVED', 'DISPENSED', 'RETURNED'].includes(linkedRequest.status)) {
                                if (!inventoryByMedicine[item.medicineName]) {
                                  inventoryByMedicine[item.medicineName] = [];
                                }
                                inventoryByMedicine[item.medicineName].push(item);
                                inventoryItemRequestMap.set(item.id, linkedRequest);
                              }
                            });
                            
                            // Track which inventory items have been assigned to treatments to prevent duplicates
                            // Map: inventoryItemId -> treatmentId (to track which treatment is using which inventory item)
                            const usedInventoryItemMap = new Map(); // inventoryItemId -> treatmentId
                            
                            // First pass: collect all explicitly linked inventory items
                            treatments.forEach(treatment => {
                              const treatmentInventoryItems = inventoryByMedicine[treatment.medicine] || [];
                              treatmentInventoryItems.forEach(inventoryItem => {
                                const linkedRequest = requestsByInventoryId[inventoryItem.id];
                                const requestKey = `${treatment.id}-${inventoryItem.id}`;
                                
                                // If explicitly linked to this treatment, mark as used
                                if ((linkedRequest && linkedRequest.treatmentId === treatment.id) || medicineRequests[requestKey]) {
                                  usedInventoryItemMap.set(inventoryItem.id, treatment.id);
                                }
                              });
                            });
                            
                            // Show treatments and their corresponding inventory items
                            const allRows = treatments.map(treatment => {
                              // Look up treatment request - check multiple sources
                              let treatmentRequest = medicineRequests[treatment.id] || null;
                              
                              // If not found by treatmentId, try to find by treatmentId+dosage
                              if (!treatmentRequest && treatment.dosage) {
                                const dosageKey = `${treatment.id}-${treatment.dosage}`;
                                treatmentRequest = requestsByTreatmentAndDosage[dosageKey] || null;
                              }
                              
                              // If still not found, search all requests for this treatmentId
                              if (!treatmentRequest) {
                                const allRequests = Object.values(medicineRequests);
                                treatmentRequest = allRequests.find(req => req.treatmentId === treatment.id) || null;
                              }
                              
                              const treatmentInventoryItems = inventoryByMedicine[treatment.medicine] || [];
                              
                              // Filter inventory items to only show those that are explicitly linked to THIS treatment's request
                              // This prevents showing duplicate rows when multiple treatments exist for the same medicine
                              // IMPORTANT: Each treatment should only show ONE inventory item - prioritize explicit links
                              const matchingInventoryItems = [];
                              
                              // First, try to find explicitly linked inventory items (by patientInventoryId or composite key)
                              for (const inventoryItem of treatmentInventoryItems) {
                                // Skip if this inventory item is already used by another treatment
                                const usedByTreatmentId = usedInventoryItemMap.get(inventoryItem.id);
                                if (usedByTreatmentId && usedByTreatmentId !== treatment.id) {
                                  continue;
                                }
                                
                                // Get the request linked to this inventory item
                                const itemRequest = inventoryItemRequestMap.get(inventoryItem.id);
                                
                                // Check if explicitly linked to this treatment's request via patientInventoryId
                                const linkedRequest = requestsByInventoryId[inventoryItem.id];
                                if (linkedRequest && linkedRequest.treatmentId === treatment.id) {
                                  matchingInventoryItems.push(inventoryItem);
                                  usedInventoryItemMap.set(inventoryItem.id, treatment.id);
                                  break; // Only take the first explicitly linked item
                                }
                                
                                // Check composite key (treatmentId-inventoryId)
                                const requestKey = `${treatment.id}-${inventoryItem.id}`;
                                if (medicineRequests[requestKey]) {
                                  matchingInventoryItems.push(inventoryItem);
                                  usedInventoryItemMap.set(inventoryItem.id, treatment.id);
                                  break; // Only take the first explicitly linked item
                                }
                                
                                // Check if the item's linked request matches this treatment
                                if (itemRequest && itemRequest.treatmentId === treatment.id) {
                                  matchingInventoryItems.push(inventoryItem);
                                  usedInventoryItemMap.set(inventoryItem.id, treatment.id);
                                  break; // Only take the first matching item
                                }
                              }
                              
                              // If no explicitly linked items found, try matching by treatmentId and dosage
                              // But only take the FIRST matching item to avoid duplicates
                              if (matchingInventoryItems.length === 0 && treatment.dosage) {
                                for (const inventoryItem of treatmentInventoryItems) {
                                  // Skip if this inventory item is already used by another treatment
                                  const usedByTreatmentId = usedInventoryItemMap.get(inventoryItem.id);
                                  if (usedByTreatmentId && usedByTreatmentId !== treatment.id) {
                                    continue;
                                  }
                                  
                                  // Check by treatmentId and dosage - ensure the request matches this treatment
                                  if (inventoryItem.dosage === treatment.dosage) {
                                    const dosageKey = `${treatment.id}-${treatment.dosage}`;
                                    const requestByDosage = requestsByTreatmentAndDosage[dosageKey];
                                    if (requestByDosage && requestByDosage.treatmentId === treatment.id) {
                                      // Also verify the item's linked request matches this treatment
                                      const itemRequest = inventoryItemRequestMap.get(inventoryItem.id);
                                      if (!itemRequest || itemRequest.treatmentId === treatment.id) {
                                        matchingInventoryItems.push(inventoryItem);
                                        usedInventoryItemMap.set(inventoryItem.id, treatment.id);
                                        break; // Only take the first matching item
                                      }
                                    }
                                  }
                                }
                              }
                              
                              // If no matching inventory items but request is ISSUED or later, try to find unlinked inventory items
                              if (matchingInventoryItems.length === 0 && treatmentRequest && ['ISSUED', 'RECEIVED', 'DISPENSED', 'RETURNED'].includes(treatmentRequest.status)) {
                                // Look for inventory items for this medicine that aren't already used
                                const unlinkedItems = inventoryItems.filter(item => {
                                  // Must match medicine name
                                  if (item.medicineName !== treatment.medicine) return false;
                                  
                                  // Must not be already used by another treatment
                                  const usedByTreatmentId = usedInventoryItemMap.get(item.id);
                                  if (usedByTreatmentId && usedByTreatmentId !== treatment.id) {
                                    return false;
                                  }
                                  
                                  // Must not be already linked to another treatment's request
                                  const itemLinkedRequest = requestsByInventoryId[item.id];
                                  if (itemLinkedRequest && itemLinkedRequest.treatmentId !== treatment.id) {
                                    return false;
                                  }
                                  
                                  // If matching by dosage, ensure it matches
                                  if (treatment.dosage && item.dosage && item.dosage !== treatment.dosage) {
                                    return false;
                                  }
                                  
                                  return true;
                                });
                                
                                // Use the first unlinked item if found and mark it as used
                                if (unlinkedItems.length > 0) {
                                  const selectedItem = unlinkedItems[0];
                                  usedInventoryItemMap.set(selectedItem.id, treatment.id);
                                  return [{
                                    type: 'inventory',
                                    treatment,
                                    request: treatmentRequest,
                                    inventoryItem: selectedItem,
                                    sortDate: selectedItem.issuedDate ? new Date(selectedItem.issuedDate) : new Date(0)
                                  }];
                                }
                              }
                              
                              if (matchingInventoryItems.length === 0) {
                                // No matching inventory items, show treatment row only
                                return {
                                  type: 'treatment',
                                  treatment,
                                  request: treatmentRequest,
                                  inventoryItem: null,
                                  sortDate: treatment.treatmentDate ? new Date(treatment.treatmentDate) : new Date(0)
                                };
                              } else {
                                // Show inventory items that match this treatment
                                return matchingInventoryItems.map(inventoryItem => {
                                  // Look up request - use the stored request from inventoryItemRequestMap first
                                  // This ensures we use the request that was used to match this inventory item to this treatment
                                  let request = inventoryItemRequestMap.get(inventoryItem.id);
                                  
                                  // Verify the request matches this treatment
                                  if (!request || request.treatmentId !== treatment.id) {
                                    // Fallback: try other lookup methods
                                    const linkedRequest = requestsByInventoryId[inventoryItem.id];
                                    const requestKey = `${treatment.id}-${inventoryItem.id}`;
                                    
                                    // Try to find request by treatmentId+dosage if patientInventoryId matching didn't work
                                    let requestByDosage = null;
                                    if (treatment.dosage && inventoryItem.dosage === treatment.dosage) {
                                      const dosageKey = `${treatment.id}-${treatment.dosage}`;
                                      requestByDosage = requestsByTreatmentAndDosage[dosageKey];
                                    }
                                    
                                    // Use the linked request if it matches this treatment, otherwise try composite key, then treatmentId+dosage, then treatment-level request
                                    request = (linkedRequest && linkedRequest.treatmentId === treatment.id) ? linkedRequest :
                                             (medicineRequests[requestKey] || 
                                             (requestByDosage && requestByDosage.treatmentId === treatment.id ? requestByDosage : null) ||
                                             treatmentRequest || 
                                             null);
                                  }
                                  
                                  return {
                                    type: 'inventory',
                                    treatment,
                                    request,
                                    inventoryItem,
                                    sortDate: inventoryItem.issuedDate ? new Date(inventoryItem.issuedDate) : new Date(0)
                                  };
                                });
                              }
                            }).flat();
                            
                            // Deduplicate rows: ensure each treatment-inventory combination appears only once
                            // Also ensure each treatment only appears once (if it has an inventory item, don't show it without one)
                            const seenRows = new Set();
                            const seenTreatmentIds = new Set(); // Track treatments that already have inventory items
                            const uniqueRows = [];
                            
                            // First pass: collect rows with inventory items
                            allRows.forEach(row => {
                              if (row.inventoryItem) {
                                const rowKey = `${row.treatment.id}-${row.inventoryItem.id}`;
                                if (!seenRows.has(rowKey)) {
                                  seenRows.add(rowKey);
                                  seenTreatmentIds.add(row.treatment.id);
                                  uniqueRows.push(row);
                                }
                              }
                            });
                            
                            // Second pass: add rows without inventory items only if the treatment doesn't already have one
                            allRows.forEach(row => {
                              if (!row.inventoryItem) {
                                const rowKey = `treatment-${row.treatment.id}`;
                                if (!seenRows.has(rowKey) && !seenTreatmentIds.has(row.treatment.id)) {
                                  seenRows.add(rowKey);
                                  uniqueRows.push(row);
                                }
                              }
                            });
                            
                            // Sort all rows by date in descending order (latest first)
                            uniqueRows.sort((a, b) => b.sortDate - a.sortDate);
                            
                            if (uniqueRows.length === 0) {
                              return (
                                <TableRow>
                                  <TableCell colSpan={12} align="center">
                                    No treatments available.
                                  </TableCell>
                                </TableRow>
                              );
                            }
                            
                            return uniqueRows.map((row, idx) => {
                              const { type, treatment, request, inventoryItem } = row;
                              
                              return (
                                <TableRow key={type === 'treatment' ? `treatment-${treatment.id}` : `inventory-${inventoryItem.id}`}>
                                  <TableCell>
                                    <Typography variant="body2" fontWeight="bold">
                                      {treatment?.medicine || inventoryItem?.medicineName || '-'}
                                    </Typography>
                                  </TableCell>
                                  <TableCell>{treatment?.dosage || '-'}</TableCell>
                                  <TableCell>{request?.duration || treatment?.duration || '-'}</TableCell>
                                  <TableCell>
                                    <Typography variant="body2">
                                      {assignmentDetails?.doctorName || '-'}
                                    </Typography>
                                  </TableCell>
                                  <TableCell>{treatment?.performedByName || '-'}</TableCell>
                                  <TableCell>
                                    {treatment?.treatmentDate
                                      ? dayjs(treatment.treatmentDate).format("DD MMM YYYY")
                                      : "-"}
                                  </TableCell>
                                  <TableCell>
                                    {formatTimeValue(
                                      treatment?.createdAt,
                                      treatment?.prescriptionDate,
                                      isDateTimeArray(treatment?.prescriptionDate) ? treatment?.prescriptionDate : null,
                                      treatment?.updatedAt,
                                      treatment?.treatmentTime,
                                      isDateTimeArray(treatment?.treatmentDate) ? treatment?.treatmentDate : null,
                                      combineDateAndTime(treatment?.treatmentDate, treatment?.treatmentTime),
                                      request?.dispensedDate,
                                      request?.receivedDate,
                                      request?.issuedDate,
                                      request?.createdAt,
                                      inventoryItem?.issuedDate,
                                      inventoryItem?.createdAt
                                    )}
                                  </TableCell>
                                  {/* <TableCell>
                                    <Typography variant="body2" fontWeight="bold">
                                      {request?.totalQuantity || '-'}
                                    </Typography>
                                  </TableCell> */}
                                  <TableCell>
                                    {inventoryItem ? (
                                      <Chip 
                                        label={`${inventoryItem.remainingQuantity} units`}
                                        color={inventoryItem.remainingQuantity > 0 ? "success" : "error"}
                                        size="small"
                                      />
                                    ) : (
                                      <Chip 
                                        label="Not in Ward"
                                        color="default"
                                        size="small"
                                      />
                                    )}
                                  </TableCell>
                                  <TableCell>
                                    {inventoryItem ? (
                                      <Box>
                                        {inventoryItem.batchNumber ? (
                                          <Typography variant="caption" display="block">
                                            Batch: {inventoryItem.batchNumber}
                                          </Typography>
                                        ) : null}
                                        {inventoryItem.expiryDate ? (
                                          <Typography variant="caption" display="block">
                                            Expiry: {dayjs(inventoryItem.expiryDate).format("DD MMM YYYY")}
                                          </Typography>
                                        ) : null}
                                        {!inventoryItem.batchNumber && !inventoryItem.expiryDate && (
                                          <Typography variant="caption" color="text.secondary">
                                            N/A
                                          </Typography>
                                        )}
                                      </Box>
                                    ) : (
                                      <Typography variant="caption" color="text.secondary">
                                        N/A
                                      </Typography>
                                    )}
                                  </TableCell>
                                  <TableCell>
                                {request ? (
  <Chip 
    label={
      request.status === "DISPENSED"
        ? "ADMINISTERED"
        : request.status
    }
    color={
      request.status === 'REQUESTED' ? 'warning' :
      request.status === 'APPROVED' ? 'info' :
      request.status === 'ISSUED' ? 'success' :
      request.status === 'RECEIVED' ? 'primary' :
      request.status === 'DISPENSED' ? 'default' :
      request.status === 'REJECTED' ? 'error' :
      request.status === 'RETURNED' ? 'error' :
      'default'
    }
    size="small"
  />
) : (
                                      <Chip 
                                        label="No Request"
                                        color="default"
                                        size="small"
                                      />
                                    )}
                                  </TableCell>
                                  <TableCell>
                                    {treatment?.medicine && (
                                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                        {!request && (
                                          <Button
                                            variant="contained"
                                            color="primary"
                                            onClick={() => handleRequestMedicine(treatment)}
                                            startIcon={<SendIcon />}
                                            size="small"
                                          >
                                            Request Medicine
                                          </Button>
                                        )}
                                        {request?.status === 'REQUESTED' && (
                                          <Chip 
                                            label="Waiting for Pharmacy Approval"
                                            color="warning"
                                            icon={<MedicationIcon />}
                                            size="small"
                                          />
                                        )}
                                        {request?.status === 'APPROVED' && (
                                          <Chip 
                                            label="Approved - Waiting for Pharmacy to Issue"
                                            color="info"
                                            icon={<CheckCircleIcon />}
                                            size="small"
                                          />
                                        )}
                                        {request?.status === 'REJECTED' && (
                                          <Chip 
                                            label="Request Rejected by Pharmacy"
                                            color="error"
                                            icon={<MedicationIcon />}
                                            size="small"
                                          />
                                        )}
                                        {request?.status === 'ISSUED' && (
                                          <Button
                                            variant="contained"
                                            color="success"
                                            onClick={() => handleReceiveMedicine(treatment, inventoryItem)}
                                            startIcon={<LocalShippingIcon />}
                                            size="small"
                                          >
                                            Receive Medicine
                                          </Button>
                                        )}
                                        {request?.status === 'RECEIVED' && inventoryItem && (
                                          <Button
                                            variant="contained"
                                            color="primary"
                                            onClick={() => handleDispenseToPatient(treatment, inventoryItem)}
                                            startIcon={<MedicationIcon />}
                                            size="small"
                                            disabled={inventoryItem.remainingQuantity <= 0}
                                          >
                                            Give Medicine to Patient
                                          </Button>
                                        )}
                                        {request?.status === 'DISPENSED' && inventoryItem && (
                                          <Button
                                            variant="contained"
                                            color="primary"
                                            onClick={() => {
                                              setSelectedInventoryItem(inventoryItem);
                                              setSelectedAdministrationRequest(request);
                                              setOpenAdministrationModal(true);
                                            }}
                                            startIcon={<MedicationIcon />}
                                            size="small"
                                            disabled={inventoryItem.remainingQuantity <= 0}
                                          >
                                            Give Medicine to Patient
                                          </Button>
                                        )}
                                        {request?.status === 'RETURNED' && (
                                          <Chip 
                                            label="Medicine Returned to Pharmacy"
                                            color="warning"
                                            icon={<UndoIcon />}
                                            size="small"
                                          />
                                        )}
                                        {inventoryItem && inventoryItem.remainingQuantity > 0 && request && 
                                         (request.status === 'RECEIVED' || request.status === 'DISPENSED') && (
                                          <Button
                                            variant="outlined"
                                            color="warning"
                                            onClick={() => handleReturnPatientMedicine(treatment, inventoryItem, request)}
                                            startIcon={<UndoIcon />}
                                            size="small"
                                          >
                                            Return to Pharmacy
                                          </Button>
                                        )}
                                      </Box>
                                    )}
                                  </TableCell>
                                </TableRow>
                              );
                            });
                          })()}
                        </TableBody>
                      </Table>
                    </Box>
                  </Grid>
                </>
              )}


              {/* IV Fluids */}
{selectedSection === "IV Fluids" && (
                <>
                 
                  <Grid item xs={12}>
                    <Box sx={{ 
                      overflowX: "auto",
                      overflowY: "auto",
                      maxHeight: "400px",
                      border: "1px solid #e0e0e0",
                      borderRadius: 1,
                    }}>
                      <Table size="small" stickyHeader>
                        <TableHead>
                          <TableRow sx={{ backgroundColor: "primary.main" }}>
                            <TableCell sx={{ color: "#fff", position: "sticky", top: 0, zIndex: 1, backgroundColor: "primary.main" }}>Medicine</TableCell>
                            <TableCell sx={{ color: "#fff", position: "sticky", top: 0, zIndex: 1, backgroundColor: "primary.main" }}>Dosage</TableCell>
                            <TableCell sx={{ color: "#fff", position: "sticky", top: 0, zIndex: 1, backgroundColor: "primary.main" }}>Duration</TableCell>
                            <TableCell sx={{ color: "#fff", position: "sticky", top: 0, zIndex: 1, backgroundColor: "primary.main" }}>Prescribed By</TableCell>
                            <TableCell sx={{ color: "#fff", position: "sticky", top: 0, zIndex: 1, backgroundColor: "primary.main" }}>Performed By</TableCell>
                            <TableCell sx={{ color: "#fff", position: "sticky", top: 0, zIndex: 1, backgroundColor: "primary.main" }}>Prescribed Date</TableCell>
                            <TableCell sx={{ color: "#fff", position: "sticky", top: 0, zIndex: 1, backgroundColor: "primary.main" }}>Prescribed Time</TableCell>
                            {/* <TableCell sx={{ color: "#fff", position: "sticky", top: 0, zIndex: 1, backgroundColor: "primary.main" }}>Quantity</TableCell> */}
                            <TableCell sx={{ color: "#fff", position: "sticky", top: 0, zIndex: 1, backgroundColor: "primary.main" }}>Ward Stock</TableCell>
                            <TableCell sx={{ color: "#fff", position: "sticky", top: 0, zIndex: 1, backgroundColor: "primary.main" }}>Batch/Expiry</TableCell>
                            <TableCell sx={{ color: "#fff", position: "sticky", top: 0, zIndex: 1, backgroundColor: "primary.main" }}>Status</TableCell>
                            <TableCell sx={{ color: "#fff", position: "sticky", top: 0, zIndex: 1, backgroundColor: "primary.main" }}>Actions</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {(() => {
                            // const treatments = treatmentList || [];
                          const treatments = ivFluidTreatments;

                            const inventoryItems = patientMedicineInventory || [];
                            
                            // Create a map of inventory items by ID for quick lookup
                            const inventoryById = {};
                            inventoryItems.forEach(item => {
                              inventoryById[item.id] = item;
                            });
                            
                            // Create a reverse map: requests by patientInventoryId or by treatmentId+dosage
                            // This helps us find which inventory items are actually linked to requests
                            const requestsByInventoryId = {};
                            const requestsByTreatmentAndDosage = {}; // Map: "treatmentId-dosage" -> request
                            
                            Object.values(medicineRequests).forEach(request => {
                              // Map by patientInventoryId if available
                              if (request.patientInventoryId) {
                                requestsByInventoryId[request.patientInventoryId] = request;
                              }
                              // Also map by treatmentId and dosage for matching when patientInventoryId is not available
                              if (request.treatmentId && request.dosage) {
                                const key = `${request.treatmentId}-${request.dosage}`;
                                requestsByTreatmentAndDosage[key] = request;
                              }
                            });
                            
                            // Create a map of inventory items by medicine name
                            // Match items to requests by patientInventoryId or by treatmentId+dosage
                            // Store the linked request with each inventory item for later use
                            const inventoryByMedicine = {};
                            const inventoryItemRequestMap = new Map(); // Map: inventoryItemId -> request
                            
                            inventoryItems.forEach(item => {
                              let linkedRequest = null;
                              
                              // First try to find by patientInventoryId
                              if (requestsByInventoryId[item.id]) {
                                linkedRequest = requestsByInventoryId[item.id];
                              } else {
                                // If not found, try to match by treatmentId and dosage
                                // Find ALL treatments that match this inventory item by medicine and dosage
                                const matchingTreatments = treatments.filter(t => 
                                  t.medicine === item.medicineName && 
                                  t.dosage === item.dosage
                                );
                                
                                // For each matching treatment, check if there's a request
                                // Prefer the one that's explicitly linked
                                for (const treatment of matchingTreatments) {
                                  const key = `${treatment.id}-${item.dosage}`;
                                  const requestByDosage = requestsByTreatmentAndDosage[key];
                                  if (requestByDosage && requestByDosage.treatmentId === treatment.id) {
                                    linkedRequest = requestByDosage;
                                    break; // Use the first matching request
                                  }
                                }
                              }
                              
                              // Only include if linked to a request with status ISSUED or later
                              if (linkedRequest && ['ISSUED', 'RECEIVED', 'DISPENSED', 'RETURNED'].includes(linkedRequest.status)) {
                                if (!inventoryByMedicine[item.medicineName]) {
                                  inventoryByMedicine[item.medicineName] = [];
                                }
                                inventoryByMedicine[item.medicineName].push(item);
                                inventoryItemRequestMap.set(item.id, linkedRequest);
                              }
                            });
                            
                            // Track which inventory items have been assigned to treatments to prevent duplicates
                            // Map: inventoryItemId -> treatmentId (to track which treatment is using which inventory item)
                            const usedInventoryItemMap = new Map(); // inventoryItemId -> treatmentId
                            
                            // First pass: collect all explicitly linked inventory items
                            treatments.forEach(treatment => {
                              const treatmentInventoryItems = inventoryByMedicine[treatment.medicine] || [];
                              treatmentInventoryItems.forEach(inventoryItem => {
                                const linkedRequest = requestsByInventoryId[inventoryItem.id];
                                const requestKey = `${treatment.id}-${inventoryItem.id}`;
                                
                                // If explicitly linked to this treatment, mark as used
                                if ((linkedRequest && linkedRequest.treatmentId === treatment.id) || medicineRequests[requestKey]) {
                                  usedInventoryItemMap.set(inventoryItem.id, treatment.id);
                                }
                              });
                            });
                            
                            // Show treatments and their corresponding inventory items
                            const allRows = treatments.map(treatment => {
                              // Look up treatment request - check multiple sources
                              let treatmentRequest = medicineRequests[treatment.id] || null;
                              
                              // If not found by treatmentId, try to find by treatmentId+dosage
                              if (!treatmentRequest && treatment.dosage) {
                                const dosageKey = `${treatment.id}-${treatment.dosage}`;
                                treatmentRequest = requestsByTreatmentAndDosage[dosageKey] || null;
                              }
                              
                              // If still not found, search all requests for this treatmentId
                              if (!treatmentRequest) {
                                const allRequests = Object.values(medicineRequests);
                                treatmentRequest = allRequests.find(req => req.treatmentId === treatment.id) || null;
                              }
                              
                              const treatmentInventoryItems = inventoryByMedicine[treatment.medicine] || [];
                              
                              // Filter inventory items to only show those that are explicitly linked to THIS treatment's request
                              // This prevents showing duplicate rows when multiple treatments exist for the same medicine
                              // IMPORTANT: Each treatment should only show ONE inventory item - prioritize explicit links
                              const matchingInventoryItems = [];
                              
                              // First, try to find explicitly linked inventory items (by patientInventoryId or composite key)
                              for (const inventoryItem of treatmentInventoryItems) {
                                // Skip if this inventory item is already used by another treatment
                                const usedByTreatmentId = usedInventoryItemMap.get(inventoryItem.id);
                                if (usedByTreatmentId && usedByTreatmentId !== treatment.id) {
                                  continue;
                                }
                                
                                // Get the request linked to this inventory item
                                const itemRequest = inventoryItemRequestMap.get(inventoryItem.id);
                                
                                // Check if explicitly linked to this treatment's request via patientInventoryId
                                const linkedRequest = requestsByInventoryId[inventoryItem.id];
                                if (linkedRequest && linkedRequest.treatmentId === treatment.id) {
                                  matchingInventoryItems.push(inventoryItem);
                                  usedInventoryItemMap.set(inventoryItem.id, treatment.id);
                                  break; // Only take the first explicitly linked item
                                }
                                
                                // Check composite key (treatmentId-inventoryId)
                                const requestKey = `${treatment.id}-${inventoryItem.id}`;
                                if (medicineRequests[requestKey]) {
                                  matchingInventoryItems.push(inventoryItem);
                                  usedInventoryItemMap.set(inventoryItem.id, treatment.id);
                                  break; // Only take the first explicitly linked item
                                }
                                
                                // Check if the item's linked request matches this treatment
                                if (itemRequest && itemRequest.treatmentId === treatment.id) {
                                  matchingInventoryItems.push(inventoryItem);
                                  usedInventoryItemMap.set(inventoryItem.id, treatment.id);
                                  break; // Only take the first matching item
                                }
                              }
                              
                              // If no explicitly linked items found, try matching by treatmentId and dosage
                              // But only take the FIRST matching item to avoid duplicates
                              if (matchingInventoryItems.length === 0 && treatment.dosage) {
                                for (const inventoryItem of treatmentInventoryItems) {
                                  // Skip if this inventory item is already used by another treatment
                                  const usedByTreatmentId = usedInventoryItemMap.get(inventoryItem.id);
                                  if (usedByTreatmentId && usedByTreatmentId !== treatment.id) {
                                    continue;
                                  }
                                  
                                  // Check by treatmentId and dosage - ensure the request matches this treatment
                                  if (inventoryItem.dosage === treatment.dosage) {
                                    const dosageKey = `${treatment.id}-${treatment.dosage}`;
                                    const requestByDosage = requestsByTreatmentAndDosage[dosageKey];
                                    if (requestByDosage && requestByDosage.treatmentId === treatment.id) {
                                      // Also verify the item's linked request matches this treatment
                                      const itemRequest = inventoryItemRequestMap.get(inventoryItem.id);
                                      if (!itemRequest || itemRequest.treatmentId === treatment.id) {
                                        matchingInventoryItems.push(inventoryItem);
                                        usedInventoryItemMap.set(inventoryItem.id, treatment.id);
                                        break; // Only take the first matching item
                                      }
                                    }
                                  }
                                }
                              }
                              
                              // If no matching inventory items but request is ISSUED or later, try to find unlinked inventory items
                              if (matchingInventoryItems.length === 0 && treatmentRequest && ['ISSUED', 'RECEIVED', 'DISPENSED', 'RETURNED'].includes(treatmentRequest.status)) {
                                // Look for inventory items for this medicine that aren't already used
                                const unlinkedItems = inventoryItems.filter(item => {
                                  // Must match medicine name
                                  if (item.medicineName !== treatment.medicine) return false;
                                  
                                  // Must not be already used by another treatment
                                  const usedByTreatmentId = usedInventoryItemMap.get(item.id);
                                  if (usedByTreatmentId && usedByTreatmentId !== treatment.id) {
                                    return false;
                                  }
                                  
                                  // Must not be already linked to another treatment's request
                                  const itemLinkedRequest = requestsByInventoryId[item.id];
                                  if (itemLinkedRequest && itemLinkedRequest.treatmentId !== treatment.id) {
                                    return false;
                                  }
                                  
                                  // If matching by dosage, ensure it matches
                                  if (treatment.dosage && item.dosage && item.dosage !== treatment.dosage) {
                                    return false;
                                  }
                                  
                                  return true;
                                });
                                
                                // Use the first unlinked item if found and mark it as used
                                if (unlinkedItems.length > 0) {
                                  const selectedItem = unlinkedItems[0];
                                  usedInventoryItemMap.set(selectedItem.id, treatment.id);
                                  return [{
                                    type: 'inventory',
                                    treatment,
                                    request: treatmentRequest,
                                    inventoryItem: selectedItem,
                                    sortDate: selectedItem.issuedDate ? new Date(selectedItem.issuedDate) : new Date(0)
                                  }];
                                }
                              }
                              
                              if (matchingInventoryItems.length === 0) {
                                // No matching inventory items, show treatment row only
                                return {
                                  type: 'treatment',
                                  treatment,
                                  request: treatmentRequest,
                                  inventoryItem: null,
                                  sortDate: treatment.treatmentDate ? new Date(treatment.treatmentDate) : new Date(0)
                                };
                              } else {
                                // Show inventory items that match this treatment
                                return matchingInventoryItems.map(inventoryItem => {
                                  // Look up request - use the stored request from inventoryItemRequestMap first
                                  // This ensures we use the request that was used to match this inventory item to this treatment
                                  let request = inventoryItemRequestMap.get(inventoryItem.id);
                                  
                                  // Verify the request matches this treatment
                                  if (!request || request.treatmentId !== treatment.id) {
                                    // Fallback: try other lookup methods
                                    const linkedRequest = requestsByInventoryId[inventoryItem.id];
                                    const requestKey = `${treatment.id}-${inventoryItem.id}`;
                                    
                                    // Try to find request by treatmentId+dosage if patientInventoryId matching didn't work
                                    let requestByDosage = null;
                                    if (treatment.dosage && inventoryItem.dosage === treatment.dosage) {
                                      const dosageKey = `${treatment.id}-${treatment.dosage}`;
                                      requestByDosage = requestsByTreatmentAndDosage[dosageKey];
                                    }
                                    
                                    // Use the linked request if it matches this treatment, otherwise try composite key, then treatmentId+dosage, then treatment-level request
                                    request = (linkedRequest && linkedRequest.treatmentId === treatment.id) ? linkedRequest :
                                             (medicineRequests[requestKey] || 
                                             (requestByDosage && requestByDosage.treatmentId === treatment.id ? requestByDosage : null) ||
                                             treatmentRequest || 
                                             null);
                                  }
                                  
                                  return {
                                    type: 'inventory',
                                    treatment,
                                    request,
                                    inventoryItem,
                                    sortDate: inventoryItem.issuedDate ? new Date(inventoryItem.issuedDate) : new Date(0)
                                  };
                                });
                              }
                            }).flat();
                            
                            // Deduplicate rows: ensure each treatment-inventory combination appears only once
                            // Also ensure each treatment only appears once (if it has an inventory item, don't show it without one)
                            const seenRows = new Set();
                            const seenTreatmentIds = new Set(); // Track treatments that already have inventory items
                            const uniqueRows = [];
                            
                            // First pass: collect rows with inventory items
                            allRows.forEach(row => {
                              if (row.inventoryItem) {
                                const rowKey = `${row.treatment.id}-${row.inventoryItem.id}`;
                                if (!seenRows.has(rowKey)) {
                                  seenRows.add(rowKey);
                                  seenTreatmentIds.add(row.treatment.id);
                                  uniqueRows.push(row);
                                }
                              }
                            });
                            
                            // Second pass: add rows without inventory items only if the treatment doesn't already have one
                            allRows.forEach(row => {
                              if (!row.inventoryItem) {
                                const rowKey = `treatment-${row.treatment.id}`;
                                if (!seenRows.has(rowKey) && !seenTreatmentIds.has(row.treatment.id)) {
                                  seenRows.add(rowKey);
                                  uniqueRows.push(row);
                                }
                              }
                            });
                            
                            // Sort all rows by date in descending order (latest first)
                            uniqueRows.sort((a, b) => b.sortDate - a.sortDate);
                            
                            if (uniqueRows.length === 0) {
                              return (
                                <TableRow>
                                  <TableCell colSpan={12} align="center">
                                    No treatments available.
                                  </TableCell>
                                </TableRow>
                              );
                            }
                            
                            return uniqueRows.map((row, idx) => {
                              const { type, treatment, request, inventoryItem } = row;
                              
                              return (
                                <TableRow key={type === 'treatment' ? `treatment-${treatment.id}` : `inventory-${inventoryItem.id}`}>
                                  <TableCell>
                                    <Typography variant="body2" fontWeight="bold">
                                      {treatment?.medicine || inventoryItem?.medicineName || '-'}
                                    </Typography>
                                  </TableCell>
                                  <TableCell>{treatment?.dosage || '-'}</TableCell>
                                  <TableCell>{request?.duration || treatment?.duration || '-'}</TableCell>
                                  <TableCell>
                                    <Typography variant="body2">
                                      {assignmentDetails?.doctorName || '-'}
                                    </Typography>
                                  </TableCell>
                                  <TableCell>{treatment?.performedByName || '-'}</TableCell>
                                  <TableCell>
                                    {treatment?.treatmentDate
                                      ? dayjs(treatment.treatmentDate).format("DD MMM YYYY")
                                      : "-"}
                                  </TableCell>
                                  <TableCell>
                                    {formatTimeValue(
                                      treatment?.createdAt,
                                      treatment?.prescriptionDate,
                                      isDateTimeArray(treatment?.prescriptionDate) ? treatment?.prescriptionDate : null,
                                      treatment?.updatedAt,
                                      treatment?.treatmentTime,
                                      isDateTimeArray(treatment?.treatmentDate) ? treatment?.treatmentDate : null,
                                      combineDateAndTime(treatment?.treatmentDate, treatment?.treatmentTime),
                                      request?.dispensedDate,
                                      request?.receivedDate,
                                      request?.issuedDate,
                                      request?.createdAt,
                                      inventoryItem?.issuedDate,
                                      inventoryItem?.createdAt
                                    )}
                                  </TableCell>
                                  {/* <TableCell>
                                    <Typography variant="body2" fontWeight="bold">
                                      {request?.totalQuantity || '-'}
                                    </Typography>
                                  </TableCell> */}
                                  <TableCell>
                                    {inventoryItem ? (
                                      <Chip 
                                        label={`${inventoryItem.remainingQuantity} units`}
                                        color={inventoryItem.remainingQuantity > 0 ? "success" : "error"}
                                        size="small"
                                      />
                                    ) : (
                                      <Chip 
                                        label="Not in Ward"
                                        color="default"
                                        size="small"
                                      />
                                    )}
                                  </TableCell>
                                  <TableCell>
                                    {inventoryItem ? (
                                      <Box>
                                        {inventoryItem.batchNumber ? (
                                          <Typography variant="caption" display="block">
                                            Batch: {inventoryItem.batchNumber}
                                          </Typography>
                                        ) : null}
                                        {inventoryItem.expiryDate ? (
                                          <Typography variant="caption" display="block">
                                            Expiry: {dayjs(inventoryItem.expiryDate).format("DD MMM YYYY")}
                                          </Typography>
                                        ) : null}
                                        {!inventoryItem.batchNumber && !inventoryItem.expiryDate && (
                                          <Typography variant="caption" color="text.secondary">
                                            N/A
                                          </Typography>
                                        )}
                                      </Box>
                                    ) : (
                                      <Typography variant="caption" color="text.secondary">
                                        N/A
                                      </Typography>
                                    )}
                                  </TableCell>
                                  <TableCell>
                                {request ? (
  <Chip 
    label={
      request.status === "DISPENSED"
        ? "ADMINISTERED"
        : request.status
    }
    color={
      request.status === 'REQUESTED' ? 'warning' :
      request.status === 'APPROVED' ? 'info' :
      request.status === 'ISSUED' ? 'success' :
      request.status === 'RECEIVED' ? 'primary' :
      request.status === 'DISPENSED' ? 'default' :
      request.status === 'REJECTED' ? 'error' :
      request.status === 'RETURNED' ? 'error' :
      'default'
    }
    size="small"
  />
) : (
                                      <Chip 
                                        label="No Request"
                                      color="default"
                                        size="small"
                                      />
                                    )}
                                  </TableCell>
                                  <TableCell>
                                    {treatment?.medicine && (
                                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                        {!request && (
                                          <Button
                                            variant="contained"
                                            color="primary"
                                            onClick={() => handleRequestMedicine(treatment)}
                                            startIcon={<SendIcon />}
                                            size="small"
                                          >
                                            Request Medicine
                                          </Button>
                                        )}
                                        {request?.status === 'REQUESTED' && (
                                          <Chip 
                                            label="Waiting for Pharmacy Approval"
                                            color="warning"
                                            icon={<MedicationIcon />}
                                            size="small"
                                          />
                                        )}
                                        {request?.status === 'APPROVED' && (
                                          <Chip 
                                            label="Approved - Waiting for Pharmacy to Issue"
                                            color="info"
                                            icon={<CheckCircleIcon />}
                                            size="small"
                                          />
                                        )}
                                        {request?.status === 'REJECTED' && (
                                          <Chip 
                                            label="Request Rejected by Pharmacy"
                                            color="error"
                                            icon={<MedicationIcon />}
                                            size="small"
                                          />
                                        )}
                                        {request?.status === 'ISSUED' && (
                                          <Button
                                            variant="contained"
                                            color="success"
                                            onClick={() => handleReceiveMedicine(treatment, inventoryItem)}
                                            startIcon={<LocalShippingIcon />}
                                            size="small"
                                          >
                                            Receive Medicine
                                          </Button>
                                        )}
                                        {request?.status === 'RECEIVED' && inventoryItem && (
                                          <Button
                                            variant="contained"
                                            color="primary"
                                            onClick={() => handleDispenseToPatient(treatment, inventoryItem)}
                                            startIcon={<MedicationIcon />}
                                            size="small"
                                            disabled={inventoryItem.remainingQuantity <= 0}
                                          >
                                            Give Medicine to Patient
                                          </Button>
                                        )}
                                        {request?.status === 'DISPENSED' && inventoryItem && (
                                          <Button
                                            variant="contained"
                                            color="primary"
                                            onClick={() => {
                                              setSelectedInventoryItem(inventoryItem);
                                              setSelectedAdministrationRequest(request);
                                              setOpenAdministrationModal(true);
                                            }}
                                            startIcon={<MedicationIcon />}
                                            size="small"
                                            disabled={inventoryItem.remainingQuantity <= 0}
                                          >
                                            Give Medicine to Patient
                                          </Button>
                                        )}
                                        {request?.status === 'RETURNED' && (
                                          <Chip 
                                            label="Medicine Returned to Pharmacy"
                                            color="warning"
                                            icon={<UndoIcon />}
                                            size="small"
                                          />
                                        )}
                                        {inventoryItem && inventoryItem.remainingQuantity > 0 && request && 
                                         (request.status === 'RECEIVED' || request.status === 'DISPENSED') && (
                                          <Button
                                            variant="outlined"
                                            color="warning"
                                            onClick={() => handleReturnPatientMedicine(treatment, inventoryItem, request)}
                                            startIcon={<UndoIcon />}
                                            size="small"
                                          >
                                            Return to Pharmacy
                                          </Button>
                                        )}
                                      </Box>
                                    )}
                                  </TableCell>
                                </TableRow>
                              );
                            });
                          })()}
                        </TableBody>
                      </Table>
                    </Box>
                  </Grid>
                </>
              )}
              {/* Nutrition */}
              {selectedSection === "Nutrition / Diet Plan" && (
                <>
                  <Grid item xs={12}>
                    <Typography variant="h6" sx={{ mt: 2, mb: 2 }}>
                      Nutrition / Diet Plan
                    </Typography>
                  </Grid>

                  {formik.values.dietPlanList.map((item, index) => (
                    <React.Fragment key={index}>
                      <Grid item xs={4}>
                        <TextField
                          fullWidth
                          label="Diet Type"
                          name={`dietPlanList[${index}].dietType`}
                          value={item.dietType}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          error={
                            !!formik.errors.dietPlanList?.[index]?.dietType &&
                            !!formik.touched.dietPlanList?.[index]?.dietType
                          }
                          helperText={
                            formik.touched.dietPlanList?.[index]?.dietType &&
                            formik.errors.dietPlanList?.[index]?.dietType
                          }
                        />
                      </Grid>
                      <Grid item xs={4}>
                        <TextField
                          fullWidth
                          label="Feeding Method"
                          name={`dietPlanList[${index}].feedingMethod`}
                          value={item.feedingMethod}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          error={
                            !!formik.errors.dietPlanList?.[index]?.feedingMethod &&
                            !!formik.touched.dietPlanList?.[index]?.feedingMethod
                          }
                          helperText={
                            formik.touched.dietPlanList?.[index]?.feedingMethod &&
                            formik.errors.dietPlanList?.[index]?.feedingMethod
                          }
                        />
                      </Grid>
                      <Grid item xs={3}>
                        <TextField
                          fullWidth
                          label="Fluid Restriction"
                          name={`dietPlanList[${index}].fluidRestriction`}
                          value={item.fluidRestriction}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          error={
                            !!formik.errors.dietPlanList?.[index]?.fluidRestriction &&
                            !!formik.touched.dietPlanList?.[index]?.fluidRestriction
                          }
                          helperText={
                            formik.touched.dietPlanList?.[index]?.fluidRestriction &&
                            formik.errors.dietPlanList?.[index]?.fluidRestriction
                          }
                        />
                      </Grid>
                      <Grid item xs={1} sx={{ display: "flex", alignItems: "center" }}>
                        <IconButton
                          color="error"
                          onClick={() => {
                            const updatedList = [...formik.values.dietPlanList];
                            updatedList.splice(index, 1);
                            formik.setFieldValue("dietPlanList", updatedList);
                          }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Grid>
                    </React.Fragment>
                  ))}

                  <Grid item xs={12} sx={{ mt: 2 }}>
                    <Button
                      variant="outlined"
                      onClick={() =>
                        formik.setFieldValue("dietPlanList", [
                          ...formik.values.dietPlanList,
                          {
                            dietType: "",
                            feedingMethod: "",
                            fluidRestriction: "",
                          },
                        ])
                      }
                    >
                      + Add More
                    </Button>
                  </Grid>
                </>
              )}

              {/* Elimination */}
              {selectedSection === "Elimination / Output" && (
                <>
                  <Grid item xs={12}>
                    <Typography variant="h6" sx={{ mt: 2, mb: 2 }}>
                      Elimination / Output
                    </Typography>
                  </Grid>

                  {formik.values.eliminationList.map((entry, index) => (
                    <React.Fragment key={index}>
                      <Grid item xs={4}>
                        <TextField
                          fullWidth
                          name={`eliminationList[${index}].urine`}
                          label="Urine"
                          value={entry.urine}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          error={
                            formik.touched.eliminationList?.[index]?.urine &&
                            Boolean(formik.errors.eliminationList?.[index]?.urine)
                          }
                          helperText={
                            formik.touched.eliminationList?.[index]?.urine &&
                            formik.errors.eliminationList?.[index]?.urine
                          }
                        />
                      </Grid>
                      <Grid item xs={4}>
                        <TextField
                          fullWidth
                          name={`eliminationList[${index}].stool`}
                          label="Stool"
                          value={entry.stool}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          error={
                            formik.touched.eliminationList?.[index]?.stool &&
                            Boolean(formik.errors.eliminationList?.[index]?.stool)
                          }
                          helperText={
                            formik.touched.eliminationList?.[index]?.stool &&
                            formik.errors.eliminationList?.[index]?.stool
                          }
                        />
                      </Grid>
                      <Grid item xs={3}>
                        <TextField
                          fullWidth
                          name={`eliminationList[${index}].vomitingOrDrains`}
                          label="Vomiting / Drains"
                          value={entry.vomitingOrDrains}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          error={
                            formik.touched.eliminationList?.[index]?.vomitingOrDrains &&
                            Boolean(formik.errors.eliminationList?.[index]?.vomitingOrDrains)
                          }
                          helperText={
                            formik.touched.eliminationList?.[index]?.vomitingOrDrains &&
                            formik.errors.eliminationList?.[index]?.vomitingOrDrains
                          }
                        />
                      </Grid>
                      <Grid item xs={1} sx={{ display: "flex", alignItems: "center" }}>
                        <IconButton
                          color="error"
                          onClick={() => {
                            const list = [...formik.values.eliminationList];
                            list.splice(index, 1);
                            formik.setFieldValue("eliminationList", list);
                          }}
                          disabled={formik.values.eliminationList.length === 1}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Grid>
                    </React.Fragment>
                  ))}

                  <Grid item xs={12} sx={{ mt: 2 }}>
                    <Button
                      variant="outlined"
                      onClick={() =>
                        formik.setFieldValue("eliminationList", [
                          ...formik.values.eliminationList,
                          { urine: "", stool: "", vomiting_or_drains: "" },
                        ])
                      }
                    >
                      + Add More
                    </Button>
                  </Grid>
                </>
              )}

              {/* Investigations */}
              {selectedSection === "Investigations" && (
                <>
                  <Grid item xs={12}>
                    <Typography variant="h6" sx={{ mt: 2, mb: 2 }}>
                      Investigations
                    </Typography>
                  </Grid>

                  {formik.values.investigationsList.map((inv, index) => (
                    <React.Fragment key={index}>
                      <Grid item xs={3}>
                        <TextField
                          fullWidth
                          select
                          name={`investigationsList[${index}].testName`}
                          label="Test Name"
                          value={inv.testName}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          error={Boolean(
                            formik.touched.investigationsList?.[index]?.testName &&
                            formik.errors.investigationsList?.[index]?.testName
                          )}
                          helperText={
                            formik.touched.investigationsList?.[index]?.testName &&
                            formik.errors.investigationsList?.[index]?.testName
                          }
                        >
                          {labTestListFromBase.length > 0 ? (
                            labTestListFromBase.map((test, i) => (
                              <MenuItem key={test.id || i} value={test.name || test.testName}>
                                {test.name || test.testName}
                              </MenuItem>
                            ))
                          ) : (
                            <MenuItem disabled value="">
                              No lab tests available
                            </MenuItem>
                          )}
                        </TextField>
                      </Grid>

                      <Grid item xs={3}>
                        <TextField
                          select
                          fullWidth
                          name={`investigationsList[${index}].status`}
                          label="Status"
                          value={inv.status}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          error={Boolean(
                            formik.touched.investigationsList?.[index]?.status &&
                            formik.errors.investigationsList?.[index]?.status
                          )}
                          helperText={
                            formik.touched.investigationsList?.[index]?.status &&
                            formik.errors.investigationsList?.[index]?.status
                          }
                        >
                          <MenuItem value="pending">Pending</MenuItem>
                          <MenuItem value="complete">Complete</MenuItem>
                        </TextField>
                      </Grid>

                      <Grid item xs={4}>
                        <TextField
                          fullWidth
                          multiline
                          rows={2}
                          name={`investigationsList[${index}].importantResults`}
                          label="Important Results"
                          value={inv.importantResults}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          error={Boolean(formik.touched.investigationsList?.[index]?.importantResults && formik.errors.investigationsList?.[index]?.importantResults)}
                          helperText={formik.touched.investigationsList?.[index]?.importantResults && formik.errors.investigationsList?.[index]?.importantResults}
                        />
                      </Grid>

                      <Grid item xs={1} sx={{ display: "flex", alignItems: "center" }}>
                        <IconButton
                          color="error"
                          onClick={() => {
                            const list = [...formik.values.investigationsList];
                            list.splice(index, 1);
                            formik.setFieldValue("investigationsList", list);
                          }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Grid>
                    </React.Fragment>
                  ))}

                  <Grid item xs={12} sx={{ mt: 2 }}>
                    <Button
                      variant="outlined"
                      onClick={() => {
                        formik.setFieldValue("investigationsList", [
                          ...formik.values.investigationsList,
                          { testName: "", status: "", importantResults: "" },
                        ]);
                      }}
                    >
                      + Add More
                    </Button>
                  </Grid>
                </>
              )}

              {/* Nursing Care Plan */}
              {selectedSection === "Nursing Care Plan" && (
                <>
                  <Grid item xs={12}>
                    <Typography variant="h6" sx={{ mt: 2, mb: 2 }}>
                      Nursing Care Plan
                    </Typography>
                  </Grid>

                  {formik.values.nursingCareList.map((care, index) => (
                    <React.Fragment key={index}>
                      <Grid item xs={4}>
                        <TextField
                          fullWidth
                          label="Problem"
                          name={`nursingCareList[${index}].problem`}
                          value={care.problem}
                          onChange={formik.handleChange}
                          error={
                            !!formik.touched?.nursingCareList?.[index]?.problem &&
                            !!formik.errors?.nursingCareList?.[index]?.problem
                          }
                          helperText={
                            formik.touched?.nursingCareList?.[index]?.problem &&
                            formik.errors?.nursingCareList?.[index]?.problem
                          }
                        />
                      </Grid>

                      <Grid item xs={4}>
                        <TextField
                          fullWidth
                          label="Intervention"
                          name={`nursingCareList[${index}].intervention`}
                          value={care.intervention}
                          onChange={formik.handleChange}
                          error={
                            !!formik.touched?.nursingCareList?.[index]?.intervention &&
                            !!formik.errors?.nursingCareList?.[index]?.intervention
                          }
                          helperText={
                            formik.touched?.nursingCareList?.[index]?.intervention &&
                            formik.errors?.nursingCareList?.[index]?.intervention
                          }
                        />
                      </Grid>

                      <Grid item xs={3}>
                        <TextField
                          fullWidth
                          label="Goal"
                          name={`nursingCareList[${index}].goal`}
                          value={care.goal}
                          onChange={formik.handleChange}
                          error={
                            !!formik.touched?.nursingCareList?.[index]?.goal &&
                            !!formik.errors?.nursingCareList?.[index]?.goal
                          }
                          helperText={
                            formik.touched?.nursingCareList?.[index]?.goal &&
                            formik.errors?.nursingCareList?.[index]?.goal
                          }
                        />
                      </Grid>

                      <Grid item xs={1} sx={{ display: 'flex', alignItems: 'center' }}>
                        <IconButton onClick={() => handleRemoveNursingCare(index)} color="error">
                          <DeleteIcon />
                        </IconButton>
                      </Grid>
                    </React.Fragment>
                  ))}

                  <Grid item xs={12} sx={{ mt: 2 }}>
                    <Button variant="outlined" onClick={handleAddNursingCare}>
                      + Add More
                    </Button>
                  </Grid>
                </>
              )}

              {/* Devices Procedures */}
              {selectedSection === "Devices / Procedures" && (
                <>
                  <Grid item xs={12}>
                    <Typography variant="h6" sx={{ mt: 2, mb: 2 }}>
                      Devices / Procedures
                    </Typography>
                  </Grid>

                  {formik.values.deviceProceduresList.map((device, index) => (
                    <React.Fragment key={index}>
                      <Grid item xs={12}>
                        <Grid container spacing={2} alignItems="center">
                          <Grid item xs={2.5}>
                            <TextField
                              fullWidth
                              label="IV Lines"
                              name={`deviceProceduresList[${index}].ivLines`}
                              value={device.ivLines}
                              onChange={formik.handleChange}
                              onBlur={formik.handleBlur}
                              error={Boolean(
                                formik.touched.deviceProceduresList?.[index]?.ivLines &&
                                formik.errors.deviceProceduresList?.[index]?.ivLines
                              )}
                              helperText={
                                formik.touched.deviceProceduresList?.[index]?.ivLines &&
                                formik.errors.deviceProceduresList?.[index]?.ivLines
                              }
                              size="small"
                            />
                          </Grid>

                          <Grid item xs={2.5}>
                            <TextField
                              fullWidth
                              label="Catheters"
                              name={`deviceProceduresList[${index}].catheters`}
                              value={device.catheters}
                              onChange={formik.handleChange}
                              onBlur={formik.handleBlur}
                              error={Boolean(
                                formik.touched.deviceProceduresList?.[index]?.catheters &&
                                formik.errors.deviceProceduresList?.[index]?.catheters
                              )}
                              helperText={
                                formik.touched.deviceProceduresList?.[index]?.catheters &&
                                formik.errors.deviceProceduresList?.[index]?.catheters
                              }
                              size="small"
                            />
                          </Grid>

                          <Grid item xs={2.5}>
                            <TextField
                              fullWidth
                              label="Tubes"
                              name={`deviceProceduresList[${index}].tubes`}
                              value={device.tubes}
                              onChange={formik.handleChange}
                              onBlur={formik.handleBlur}
                              error={Boolean(
                                formik.touched.deviceProceduresList?.[index]?.tubes &&
                                formik.errors.deviceProceduresList?.[index]?.tubes
                              )}
                              helperText={
                                formik.touched.deviceProceduresList?.[index]?.tubes &&
                                formik.errors.deviceProceduresList?.[index]?.tubes
                              }
                              size="small"
                            />
                          </Grid>

                          <Grid item xs={2.5}>
                            <TextField
                              fullWidth
                              label="Drain Sites"
                              name={`deviceProceduresList[${index}].drainSites`}
                              value={device.drainSites}
                              onChange={formik.handleChange}
                              onBlur={formik.handleBlur}
                              error={Boolean(
                                formik.touched.deviceProceduresList?.[index]?.drainSites &&
                                formik.errors.deviceProceduresList?.[index]?.drainSites
                              )}
                              helperText={
                                formik.touched.deviceProceduresList?.[index]?.drainSites &&
                                formik.errors.deviceProceduresList?.[index]?.drainSites
                              }
                              size="small"
                            />
                          </Grid>

                          <Grid item xs={0.5} sx={{ display: "flex", alignItems: "center" }}>
                            <IconButton
                              onClick={() => {
                                const list = [...formik.values.deviceProceduresList];
                                list.splice(index, 1);
                                formik.setFieldValue("deviceProceduresList", list);
                              }}
                              color="error"
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Grid>
                        </Grid>
                      </Grid>
                    </React.Fragment>
                  ))}

                  <Grid item xs={12} sx={{ mt: 2 }}>
                    <Button variant="outlined" onClick={handleAddDevice}>
                      + Add More
                    </Button>
                  </Grid>
                </>
              )}

              {/* Activity / Mobility */}
              {selectedSection === "Activity / Mobility" && (
                <>
                  <Grid item xs={12}>
                    <Typography variant="h6" sx={{ mt: 2, mb: 2 }}>
                      Activity / Mobility 
                    </Typography>
                  </Grid>

                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      label="Mobility Level"
                      name="mobilityLevel"
                      {...formik.getFieldProps("mobilityLevel")}
                      error={formik.touched.mobilityLevel && Boolean(formik.errors.mobilityLevel)}
                      helperText={formik.touched.mobilityLevel && formik.errors.mobilityLevel}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      label="Turning Schedule"
                      name="turningSchedule"
                      {...formik.getFieldProps("turningSchedule")}
                      error={formik.touched.turningSchedule && Boolean(formik.errors.turningSchedule)}
                      helperText={formik.touched.turningSchedule && formik.errors.turningSchedule}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      label="Fall Risk Precautions"
                      name="fallRiskPrecautions"
                      {...formik.getFieldProps("fallRiskPrecautions")}
                      error={formik.touched.fallRiskPrecautions && Boolean(formik.errors.fallRiskPrecautions)}
                      helperText={formik.touched.fallRiskPrecautions && formik.errors.fallRiskPrecautions}
                    />
                  </Grid>
                </>
              )}

              {/* Shift-wise entry */}
              {selectedSection === "Shift-wise Nursing Notes" && (
                <>
                  <Grid item xs={12}>
                    <Typography variant="h6" sx={{ mt: 2, mb: 2 }}>
                      Shift-wise Nursing Notes
                    </Typography>
                  </Grid>

                  <Grid item xs={6}>
                    <TextField
                      select
                      fullWidth
                      label="Shift"
                      name="shift"
                      {...formik.getFieldProps("shift")}
                      error={formik.touched.shift && Boolean(formik.errors.shift)}
                      helperText={formik.touched.shift && formik.errors.shift}
                    >
                      {["Morning", "Afternoon", "Night"].map((option) => (
                        <MenuItem key={option} value={option}>
                          {option}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>

                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      multiline
                      rows={4}
                      label="Observations / Interventions"
                      name="observationsInterventions"
                      {...formik.getFieldProps("observationsInterventions")}
                      error={
                        formik.touched.observationsInterventions &&
                        Boolean(formik.errors.observationsInterventions)
                      }
                      helperText={
                        formik.touched.observationsInterventions &&
                        formik.errors.observationsInterventions
                      }
                    />
                  </Grid>
                </>
              )}

              {/* Education / Communication */}
              {selectedSection === "Patient Education & Communication" && (
                <>
                  <Grid item xs={12}>
                    <Typography variant="h6" sx={{ mt: 2, mb: 2 }}>
                      Education / Communication
                    </Typography>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Education Provided"
                      name="educationProvided"
                      value={formik.values.educationProvided}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={formik.touched.educationProvided && Boolean(formik.errors.educationProvided)}
                      helperText={formik.touched.educationProvided && formik.errors.educationProvided}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Family Counseling"
                      name="familyCounseling"
                      value={formik.values.familyCounseling}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={formik.touched.familyCounseling && Boolean(formik.errors.familyCounseling)}
                      helperText={formik.touched.familyCounseling && formik.errors.familyCounseling}
                    />
                  </Grid>
                </>
              )}

              {/* Miscellaneous */}
              {selectedSection === "Miscellaneous / Alerts" && (
                <>
                  <Grid item xs={12}>
                    <Typography variant="h6" sx={{ mt: 2, mb: 2 }}>
                      Miscellaneous / Alerts
                    </Typography>
                  </Grid>

                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      multiline
                      rows={3}
                      label="Special Notes"
                      name="specialNotes"
                      value={formik.values.specialNotes}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={formik.touched.specialNotes && Boolean(formik.errors.specialNotes)}
                      helperText={formik.touched.specialNotes && formik.errors.specialNotes}
                    />
                  </Grid>

                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      multiline
                      rows={2}
                      label="DNR (Do Not Resuscitate)"
                      name="dnr"
                      value={formik.values.dnr}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={formik.touched.dnr && Boolean(formik.errors.dnr)}
                      helperText={formik.touched.dnr && formik.errors.dnr}
                    />
                  </Grid>

                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      multiline
                      rows={3}
                      label="Discharge Plans"
                      name="dischargePlans"
                      value={formik.values.dischargePlans}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={formik.touched.dischargePlans && Boolean(formik.errors.dischargePlans)}
                      helperText={formik.touched.dischargePlans && formik.errors.dischargePlans}
                    />
                  </Grid>
                </>
              )}

            </Grid>
          </Box>

         {selectedSection !== "Medications" &&
 selectedSection !== "IV Fluids" && (
  <FormModalActions
    onCancel={onClose}
    submitType="submit"
    loading={formik.isSubmitting}
  />
)}

       
        </form>
      </Box>
      
      {/* Medicine Request Modal */}
      <MedicineRequestModal
        open={openRequestModal}
        onClose={handleRequestModalClose}
        treatment={selectedRequestTreatment}
        onSubmit={handleRequestSubmit}
        patientId={patient_info?.id}
        admissionId={admissionId}
      />

      {/* Medicine Return Modal */}
      <MedicineReturnModal
        open={openReturnModal}
        onClose={handleReturnModalClose}
        treatment={selectedReturnTreatment}
        onSubmit={handleReturnSubmit}
        patientId={patient_info?.id}
        medicineRequest={selectedReturnTreatment?.medicineRequest}
      />

      {/* Patient Medicine Administration Modal */}
      <PatientMedicineAdministrationModal
        open={openAdministrationModal}
        onClose={handleAdministrationModalClose}
        patientInventory={selectedInventoryItem}
        onSubmit={handleAdministrationSubmit}
        patientId={patient_info?.id}
        admissionId={admissionId}
      />
    </BasicModal>
  );
};

export default NurseKardexForm;